/**
 * VitalsBand — the at-a-glance clinical facts strip, composed BELOW the
 * EntityHeader (PR-C1 §4.2). Not a parallel page header: EntityHeader owns
 * identity/name/eyebrow/actions; this renders the structured vitals facts
 * (status, code_status, ELEVATED risk pills, coverage summary, referred-by,
 * last-touched) as a ruled sub-band.
 *
 * Record-add-assign slice (founder soak feedback 2026-07-06): the CARE
 * MANAGER fact is now editable — always rendered, with an Assign/Change
 * affordance opening a picker of ACTIVE care managers (the
 * useAssignOptions / AddClientDialog source), writing
 * clients.primary_cm_roster_id via POST /clients/{id}/assign-cm (change_log
 * trail server-side). Success refetches the record aggregate AND the roster
 * list. This band renders only inside the record page, which gates on
 * view_all_clinical (the soak surface gate) — same gate as the API route.
 *
 * Tokens only (sand / sage / gold-soft / ink-soft). No raw greys.
 */

import { useLayoutEffect, useRef, useState, type ChangeEvent, type KeyboardEvent, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '@/components/primitives/Button';
import { Dialog } from '@/components/primitives/Dialog';
import { Field, fieldBoxAria } from '@/components/primitives/Field';
import { FLD, FLD_TEXTAREA } from '@/components/primitives/modalFormStyles';
import { SelectField } from '@/components/primitives/SelectField';
import { TextInput } from '@/components/primitives/TextInput';
import { SegmentedToggle, type SegmentedOption } from '@/components/primitives/SegmentedToggle';
import { DirectoryNameBox } from '@/components/clientRecord/DirectoryNameBox';
import { proseDate, proseDayOrDate } from '@/lib/proseDate';
import { useToday } from '@/lib/today';
import { useTenant } from '@/lib/auth';
import { isSpiceOnlyViewer } from '@/lib/spiceOnlyViewer';
import {
  canCheckAddress,
  canEditClientDetails,
  canEditClientSchedule,
  canManageClientIdentity,
  canWriteClinicalRecord,
} from '@/lib/capabilities';
import { patchTransportationConsent, recordEditErrorMessage } from '@/lib/clientRecordEditApi';
import { consentIntro } from '@/lib/recordFormsCopy';
import { useAssignOptions } from '@/lib/workspace';
import {
  nextSpiceEngagement,
  useSetClientDemographics,
  useSetClientEngagement,
  useSetClientLifecycleStatus,
  useSetClientName,
  useSetClientPronouns,
  type ClientEngagementValue,
} from '@/lib/clientsApi';
import {
  spiceVitalsToInput,
  useDeleteClientSpicePlan,
  useSetClientSpicePlan,
  type SpiceScheduleVitals,
  type SpiceSessionFrequency,
  type SpiceSessionKind,
  type SpiceVisitPlanInput,
  type SpiceWeekOfMonth,
} from '@/lib/spiceVisitPlansApi';
import {
  SPICE_DAY_NAMES,
  SPICE_FREQUENCY_TITLES,
  SPICE_KIND_LABELS,
  SPICE_WEEK_OF_MONTH_LABELS,
  formatSpiceShortDate,
  type SpiceScheduleSession,
} from '@/lib/spiceScheduleCopy';
import { useSpiceKindColors } from '@/hooks/useSpiceKindColors';
import { spiceSessionsClash } from '@/lib/spiceScheduleMove';
import { weekOfMonthForDrop } from '@/lib/spiceNextSession';
import { SpiceKindDot } from '@/components/clientRecord/SpiceKindMark';
import { practiceDayOfStamp, practiceTodayIso } from '@/lib/practiceDate';
import { SpiceScheduleSummary } from '@/components/clientRecord/SpiceScheduleSummary';
import {
  CLIENT_GENDER_VALUES,
  RELATIONSHIP_STATUS_VALUES,
  CLIENT_LIFECYCLE_STATUS_CHOICES,
  CLIENT_PRONOUN_VALUES,
  TRANSPORTATION_CONSENT_VALUES,
  type PayloadFieldSpec,
} from '@/lib/noteVocabulary';
import { nextRadioIndex, radioStep } from '@/lib/radioGroupKeys';
import { humanizeToken } from '@/lib/fieldLabels';
import {
  AddressCheckChoices,
  AddressCheckPrompt,
  AddressFields,
  SavedLineView,
  flaggedOnOpen,
  homeFieldMessages,
  postalFieldsOf,
  postalFieldsOfRow,
  saveErrorMessage,
  savedLineFor,
  useAddressCheck,
  type AddressFieldsValues,
  type AddressSaveHow,
  type FlaggedFields,
  type SavedLine,
} from '@/components/clientRecord/AddressesCard';
import {
  useSaveClientAddress,
  useSetClientAddressArchived,
  type ClientAddressInput,
} from '@/lib/clientAddressesApi';
import { formatPhoneDisplay, formatPhoneInput, phoneProblem, normalizePhone } from '@/lib/phone';
import {
  addressDuplicateKey,
  addressTextChanged,
  addressLine,
  isPoBoxLine,
  missingHomeFields,
  newAddressStartState,
  zipProblem,
} from '@/lib/address';
import { NO_PRIMARY_TEXT, currentlyAt, homeFact } from '@/lib/clientLocation';
import { readLanguages, readWorkingStates } from '@/lib/tenantConfig';
import { useTenantConfig } from '@/hooks/useTenantConfig';
import { useSupportContact } from '@/hooks/useSupportContact';
import { errorCodeOf, fieldOf, pageWriteText } from '@/lib/errorCopy';
import { fieldSentence } from '@/lib/fieldErrorCopy';
import { ageLine } from '@/lib/recordFormsCopy';
import { contactEmailProblem } from '@/lib/clientBillingContacts';
import { normalizeZip } from '@/lib/usStates';
import {
  LIFECYCLE_STATUS_EFFECT,
  LIFECYCLE_STATUS_LABELS,
  STATUS_NOTE_MAX,
  displayStatusNote,
  lifecycleBadge,
  type ClientEngagement,
  type ClientPlace,
  type AddressRow,
  type ClientLifecycleStatus,
  type TransportationConsent,
  type VitalsBand as VitalsBandData,
} from '@/lib/clientRecord';
import { VITALS_OVERVIEW_KEY } from '@/lib/keptAcrossSignOut';
import { DateInput } from '@/components/primitives/DateInput';

/** 'today' → 'Today' (standalone Fact values read capitalized). */
function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function Fact({ label, children }: { label: string; children: ReactNode }): ReactNode {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-label tracking-label font-semibold uppercase text-sage-400">
        {label}
      </span>
      {/* B5 (type courage): fact values step up half a size — the band
          carries the client's most-read facts, so it reads confident. */}
      <span className="text-value font-medium text-sage-800">{children}</span>
    </div>
  );
}

/** Split a display_name into a first/last guess for prefill: the LAST space
 *  is the boundary (everything before = first, after = last), so a
 *  multi-word first name ("Mary Jo") survives. A mononym (no space) yields
 *  an empty last. The human sees and confirms/corrects this before saving,
 *  so the guess is always human-confirmed. EXPORTED for a direct unit test. */
export function splitDisplayName(display: string): { first: string; last: string } {
  const normalized = display.trim().replace(/\s+/g, ' ');
  const idx = normalized.lastIndexOf(' ');
  if (idx === -1) return { first: normalized, last: '' };
  return { first: normalized.slice(0, idx), last: normalized.slice(idx + 1) };
}

/** Derive the display_name preview the same way the server does
 *  (assembleDisplayName): trimmed, single space, mononym → first alone. */
function deriveDisplay(first: string, last: string): string {
  return [first.trim(), last.trim()].filter(Boolean).join(' ');
}

/** The client name dialog: first / last / preferred as discrete fields.
 *  display_name is DERIVED server-side. EXPORTED: the record header owns
 *  the affordance (the menu's Edit name), gated to manage_client_identity.
 *
 *  Prefill: from the stored first/last split when firstName is present
 *  (preserves a correct multi-word split); else GUESS by splitting
 *  displayName, which the human confirms or corrects before saving.
 *  Preferred prefills from preferredName. First name is required
 *  (display_name is NOT NULL): "Save name" is pressable at rest; pressed
 *  with First blank it sends nothing, says so under First and moves focus
 *  there; pressed unchanged it closes and sends nothing. */
export function ClientNameDialog({
  currentDisplayName,
  currentFirstName,
  currentLastName,
  currentPreferredName,
  onClose,
}: {
  currentDisplayName: string;
  currentFirstName: string | null;
  currentLastName: string | null;
  currentPreferredName: string | null;
  onClose: () => void;
}): ReactNode {
  const { tenantSlug = '', clientId = '' } = useParams<{ tenantSlug: string; clientId: string }>();
  const setName = useSetClientName(tenantSlug);
  const contact = useSupportContact();

  // Prefer the stored structured split; fall back to the display_name guess.
  const prefill =
    currentFirstName !== null && currentFirstName.trim() !== ''
      ? { first: currentFirstName, last: currentLastName ?? '' }
      : splitDisplayName(currentDisplayName);
  const [first, setFirst] = useState(prefill.first);
  const [last, setLast] = useState(prefill.last);
  const [preferred, setPreferred] = useState(currentPreferredName ?? '');
  // The gap the last press found under First; the next keystroke clears it.
  const [firstGap, setFirstGap] = useState<string | null>(null);
  const firstRef = useRef<HTMLInputElement | null>(null);

  const firstTrimmed = first.trim();
  const lastTrimmed = last.trim();
  const preferredTrimmed = preferred.trim();
  // Changed vs the CURRENT stored structured values (not the prefill), so
  // confirming a legacy split (first/last were NULL) counts as a change and
  // backfills the structured columns.
  const changed =
    firstTrimmed !== (currentFirstName ?? '').trim() ||
    lastTrimmed !== (currentLastName ?? '').trim() ||
    preferredTrimmed !== (currentPreferredName ?? '').trim();

  /** "Save name": nothing changed closes; a blank First says so and keeps
   *  focus there; otherwise it saves. */
  function pressSave(): void {
    if (setName.isPending) return;
    if (!changed) {
      onClose();
      return;
    }
    if (firstTrimmed === '') {
      setFirstGap(fieldSentence('First name', { kind: 'text' }, first));
      firstRef.current?.focus();
      return;
    }
    setName.mutate(
      {
        id: clientId,
        firstName: firstTrimmed,
        lastName: lastTrimmed === '' ? null : lastTrimmed,
        preferredName: preferredTrimmed === '' ? null : preferredTrimmed,
      },
      { onSuccess: onClose },
    );
  }

  const preview = deriveDisplay(first, last);

  return (
    <Dialog
      title="Edit name"
      size="sm"
      testId="client-name-dialog"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={setName.isPending}
            loading={setName.isPending}
            data-testid="client-name-save"
            onClick={pressSave}
          >
            Save name
          </Button>
        </>
      }
    >
      <p className="text-xs text-ink-soft mb-3">First and last name show across the record and the roster.</p>
      <div className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2">
        <Field label="First name" required htmlFor="client-first-name-input" error={firstGap ?? undefined}>
          <TextInput
            ref={firstRef}
            id="client-first-name-input"
            data-testid="client-first-name-input"
            type="text"
            nameCase="person"
            autoFocus
            maxLength={100}
            className={FLD}
            value={first}
            aria-required="true"
            aria-invalid={firstGap !== null ? 'true' : undefined}
            aria-describedby={firstGap !== null ? 'client-first-name-input-error' : undefined}
            onChange={(e) => {
              setFirstGap(null);
              setFirst(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') pressSave();
            }}
          />
        </Field>
        <Field label="Last name" htmlFor="client-last-name-input">
          <TextInput
            id="client-last-name-input"
            data-testid="client-last-name-input"
            type="text"
            nameCase="person"
            maxLength={100}
            className={FLD}
            value={last}
            onChange={(e) => setLast(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') pressSave();
            }}
          />
        </Field>
        <div className="sm:col-span-2">
          <Field label="Preferred name" htmlFor="client-preferred-name-input">
            <TextInput
              id="client-preferred-name-input"
              data-testid="client-preferred-name-input"
              type="text"
              nameCase="person"
              maxLength={100}
              className={FLD}
              value={preferred}
              onChange={(e) => setPreferred(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') pressSave();
              }}
            />
          </Field>
        </div>
        {/* The derived name the human confirms before saving. */}
        <p className="text-xs text-ink-soft sm:col-span-2" data-testid="client-name-preview">
          Shows as:{' '}
          <span className="font-medium text-sage-800">
            {preview === '' ? '–' : preview}
          </span>
        </p>
      </div>
      {setName.isError && (
        <p role="alert" data-testid="client-name-error" className="mt-2 text-xs text-pepper">
          {pageWriteText(setName.error, contact)}
        </p>
      )}
    </Dialog>
  );
}

/** The pronouns picker dialog, over the closed pronoun set. "Not recorded"
 *  (empty) clears to NULL server-side. EXPORTED: pronouns render next to
 *  the CLIENT'S name in the record header, so the header owns the
 *  affordance and mounts this dialog. "Save pronouns" is pressable at
 *  rest; pressed unchanged it closes and sends nothing. */
export function PronounsDialog({
  currentPronouns,
  onClose,
}: {
  currentPronouns: string;
  onClose: () => void;
}): ReactNode {
  const { tenantSlug = '', clientId = '' } = useParams<{ tenantSlug: string; clientId: string }>();
  const setPronouns = useSetClientPronouns(tenantSlug);
  const contact = useSupportContact();
  const [pronouns, setPronounsValue] = useState(currentPronouns);

  function pressSave(): void {
    if (setPronouns.isPending) return;
    if (pronouns === currentPronouns) {
      onClose();
      return;
    }
    setPronouns.mutate({ id: clientId, pronouns: pronouns === '' ? null : pronouns }, { onSuccess: onClose });
  }

  return (
    <Dialog
      title={currentPronouns === '' ? 'Record pronouns' : 'Change pronouns'}
      size="sm"
      testId="pronouns-dialog"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={setPronouns.isPending}
            loading={setPronouns.isPending}
            data-testid="pronouns-save"
            onClick={pressSave}
          >
            Save pronouns
          </Button>
        </>
      }
    >
      <p className="text-xs text-ink-soft mb-3">Used in assessments, letters and other documents.</p>
      <Field label="Pronouns" htmlFor="pronouns-select">
        <SelectField
          size="field"
          id="pronouns-select"
          data-testid="pronouns-select"
          value={pronouns}
          onChange={(e) => setPronounsValue(e.target.value)}
        >
          <option value="">Not recorded</option>
          {CLIENT_PRONOUN_VALUES.map((v) => (
            <option key={v} value={v}>
              {v}
            </option>
          ))}
        </SelectField>
      </Field>
      {setPronouns.isError && (
        <p role="alert" data-testid="pronouns-error" className="mt-2 text-xs text-pepper">
          {pageWriteText(setPronouns.error, contact)}
        </p>
      )}
    </Dialog>
  );
}

/** The id of the Status note box, which its label and the box share. */
const STATUS_NOTE_BOX_ID = 'lifecycle-status-note';
/** The id of the radio group's error slot ("Choose a status."). */
const STATUS_NEEDED_ID = 'lifecycle-status-needed';
/** The id of the On hospice since box, which its label and the box share. */
const HOSPICE_BOX_ID = 'lifecycle-hospice-since';
const HOSPICE_FUTURE_SENTENCE = "On hospice since can't be in the future. Enter today or an earlier day.";
/** The id of the Date of death box, which its label and the box share. */
const DEATH_BOX_ID = 'lifecycle-date-of-death';
/** Under an empty Date of death while a deceased status is checked. */
export const DEATH_DATE_LATER_SENTENCE = 'The weekly call board will ask for this date.';

/** What is wrong with a changed date of death, if anything. */
type DeathProblem = 'future' | 'before_birth' | null;

const DEATH_SENTENCES: Record<'future' | 'before_birth', string> = {
  future: "Date of death can't be in the future. Enter today or an earlier day.",
  before_birth: "Date of death can't be before the date of birth. Check both dates and fix the wrong one.",
};

/** The two statuses that say the client has died. */
function isDeceasedStatus(status: ClientLifecycleStatus | null): boolean {
  return status === 'deceased' || status === 'deceased_family_work';
}

/** Sizes the Status note box to its text, so a note never scrolls inside
 *  it. The borders are added back because the box is border-box. In an
 *  environment with no layout (scrollHeight 0) the height is left alone. */
function fitStatusNoteBox(el: HTMLTextAreaElement | null): void {
  if (!el) return;
  el.style.height = 'auto';
  if (el.scrollHeight > 0) el.style.height = `${el.scrollHeight + el.offsetHeight - el.clientHeight}px`;
}

/** Change client status: the client's real-world status, its note and
 *  the one date the status calls for. A radio group of the statuses a
 *  person can set (CLIENT_LIFECYCLE_STATUS_CHOICES), each card its label
 *  (LIFECYCLE_STATUS_LABELS, the map the header chip shares) and, quieter,
 *  its effect on the client map and the caseload (LIFECYCLE_STATUS_EFFECT);
 *  one tab stop, the checked card, with arrow keys that wrap
 *  (radioGroupKeys). A client whose stored status is not among the
 *  choices opens with no card checked, the first card as the tab stop, no
 *  date field, and "Choose a status." when Save status is pressed. The
 *  Status note is one line, at most STATUS_NOTE_MAX characters, stored as
 *  typed: Enter adds no line break, a pasted line break becomes a space,
 *  the box grows with its text, and it empties every time the checked
 *  card changes (the note the dialog opened with never comes back). While
 *  the box holds a note, "Shows as" under it reads the checked label, then
 *  the shown note in brackets, as the chip will. Save status stays
 *  pressable (disabled only while sending): it sends { lifecycle_status,
 *  status_note } with null for an empty box, and closes without sending
 *  when nothing changed. A failed save reads the shared page-write
 *  sentence on the alert line. EXPORTED: the header chip owns the
 *  affordance. Orthogonal to archive: this never archives; "Archived" is
 *  the is_active=false state, changed from the record menu.
 *
 *  One date, chosen by the status (LP-25). A deceased card shows Date of
 *  death; Active or Inactive shows On hospice since; never both. Each box
 *  keeps what was typed while the other is shown, and only the shown one
 *  is sent, and only when it changed. Date of death: no later than the
 *  practice day and not before the date of birth when one is recorded,
 *  judged only when changed (a stored imported date never blocks a save);
 *  empty is allowed, with DEATH_DATE_LATER_SENTENCE under the box. It is
 *  written through the demographics write in the same transaction, and
 *  this is the one form that edits it. On hospice since (0243): the day
 *  the client went on hospice, no later than today; empty means not on
 *  hospice. The weekly call writes both fields through the same server
 *  writes. */
export function LifecycleStatusDialog({
  currentStatus,
  currentNote,
  currentHospiceSince = null,
  currentDateOfDeath = null,
  currentDob = null,
  onClose,
}: {
  currentStatus: ClientLifecycleStatus;
  currentNote: string | null;
  currentHospiceSince?: string | null;
  currentDateOfDeath?: string | null;
  currentDob?: string | null;
  onClose: () => void;
}): ReactNode {
  const { tenantSlug = '', clientId = '' } = useParams<{ tenantSlug: string; clientId: string }>();
  const setLifecycle = useSetClientLifecycleStatus(tenantSlug);
  const contact = useSupportContact();
  const choices = CLIENT_LIFECYCLE_STATUS_CHOICES as readonly ClientLifecycleStatus[];
  const [status, setStatus] = useState<ClientLifecycleStatus | null>(
    choices.includes(currentStatus) ? currentStatus : null,
  );
  const [note, setNote] = useState<string>(currentNote ?? '');
  const [statusNeeded, setStatusNeeded] = useState(false);
  const [hospice, setHospice] = useState<string>(currentHospiceSince ?? '');
  const [hospiceFuture, setHospiceFuture] = useState(false);
  const [death, setDeath] = useState<string>(currentDateOfDeath ?? '');
  // The sentence under Date of death, shown from blur or a press until the next change.
  const [deathShown, setDeathShown] = useState<DeathProblem>(null);
  const today = useToday() ?? practiceTodayIso();
  const cards = useRef<Array<HTMLButtonElement | null>>([]);
  const box = useRef<HTMLTextAreaElement | null>(null);
  const deathRef = useRef<HTMLInputElement | null>(null);

  const checkedIndex = status === null ? -1 : choices.indexOf(status);
  const tabStopIndex = checkedIndex === -1 ? 0 : checkedIndex;
  // Which one date field the checked card calls for.
  const showDeath = isDeceasedStatus(status);
  const showHospice = status !== null && !showDeath;

  const dob = currentDob ?? '';
  const deathChanged = death !== (currentDateOfDeath ?? '');
  const deathProblem: DeathProblem =
    !deathChanged || death === ''
      ? null
      : death > today
        ? 'future'
        : dob !== '' && death < dob
          ? 'before_birth'
          : null;

  useLayoutEffect(() => {
    fitStatusNoteBox(box.current);
  }, [note]);

  function check(value: ClientLifecycleStatus): void {
    if (value !== status) setNote('');
    setStatus(value);
    setStatusNeeded(false);
  }

  function onCardKey(e: KeyboardEvent<HTMLButtonElement>): void {
    const step = radioStep(e.key);
    if (step === 0) return;
    e.preventDefault();
    const next = nextRadioIndex(checkedIndex, step, choices.length);
    check(choices[next]!);
    cards.current[next]?.focus();
  }

  function onNoteChange(e: ChangeEvent<HTMLTextAreaElement>): void {
    setNote(e.target.value.replace(/\s*[\r\n]+\s*/g, ' '));
  }

  function save(): void {
    if (status === null) {
      setStatusNeeded(true);
      cards.current[0]?.focus();
      return;
    }
    if (showDeath && deathProblem !== null) {
      setDeathShown(deathProblem);
      deathRef.current?.focus();
      return;
    }
    if (showHospice && hospice !== '' && hospice > today) {
      setHospiceFuture(true);
      return;
    }
    const trimmed = note.trim();
    const hospiceChanged = showHospice && hospice !== (currentHospiceSince ?? '');
    const sendDeath = showDeath && deathChanged;
    if (status === currentStatus && trimmed === (currentNote ?? '').trim() && !hospiceChanged && !sendDeath) {
      onClose();
      return;
    }
    setLifecycle.mutate(
      {
        id: clientId,
        lifecycleStatus: status,
        statusNote: trimmed === '' ? null : trimmed,
        ...(hospiceChanged ? { onHospiceSince: hospice === '' ? null : hospice } : {}),
        ...(sendDeath ? { dateOfDeath: death === '' ? null : death } : {}),
      },
      {
        onSuccess: onClose,
        onError: (err) => {
          if (errorCodeOf(err) === 'invalid_value' && fieldOf(err) === 'date_of_death') {
            setDeathShown(death > today ? 'future' : 'before_birth');
            deathRef.current?.focus();
          }
        },
      },
    );
  }

  const previewNote = displayStatusNote(note);
  const previewLabel = lifecycleBadge(status ?? currentStatus, true, note).label;
  const deathRefused = setLifecycle.isError && errorCodeOf(setLifecycle.error) === 'invalid_value' && fieldOf(setLifecycle.error) === 'date_of_death';

  return (
    <Dialog
      title="Change client status"
      size="sm"
      testId="lifecycle-dialog"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            loading={setLifecycle.isPending}
            data-testid="lifecycle-save"
            onClick={save}
          >
            Save status
          </Button>
        </>
      }
    >
      <p className="text-xs text-ink-soft mb-2.5">Separate from archiving, which is on the record menu.</p>
      <div
        role="radiogroup"
        aria-label="Status"
        aria-describedby={statusNeeded ? STATUS_NEEDED_ID : undefined}
        className="flex flex-col gap-1"
      >
        {choices.map((value, index) => {
          const selected = status === value;
          return (
            <Button
              key={value}
              ref={(el) => {
                cards.current[index] = el;
              }}
              variant="ghost"
              role="radio"
              aria-checked={selected}
              tabIndex={index === tabStopIndex ? 0 : -1}
              data-testid={`lifecycle-option-${value}`}
              className={`!grid !h-auto !min-h-0 !w-full !grid-cols-[auto_minmax(0,1fr)] !items-start !justify-start !gap-x-2.5 !gap-y-0 rounded-card border !px-2.5 !py-1.5 text-left !whitespace-normal ${
                selected ? 'border-sage-600 !bg-sage-50' : 'border-sage-800/[12%]'
              }`}
              onClick={() => check(value)}
              onKeyDown={onCardKey}
            >
              <span
                aria-hidden="true"
                className={`row-span-2 mt-[0.125rem] inline-block h-3.5 w-3.5 shrink-0 rounded-full ${
                  selected ? 'border-[0.3125rem] border-sage-600 bg-paper' : 'border border-sage-800/[30%] bg-paper'
                }`}
              />
              <span className="min-w-0 break-words text-[0.8125rem] font-medium text-sage-800">
                {LIFECYCLE_STATUS_LABELS[value]}
              </span>
              <span className="col-start-2 min-w-0 break-words text-[0.6875rem] leading-snug font-normal text-ink-soft">
                {LIFECYCLE_STATUS_EFFECT[value]}
              </span>
            </Button>
          );
        })}
      </div>
      {statusNeeded && (
        <p id={STATUS_NEEDED_ID} role="alert" className="mt-1.5 text-xs text-pepper-deep">
          Choose a status.
        </p>
      )}
      <div className="mt-3">
        <Field label="Status note" htmlFor={STATUS_NOTE_BOX_ID}>
          <textarea
            id={STATUS_NOTE_BOX_ID}
            ref={box}
            rows={1}
            maxLength={STATUS_NOTE_MAX}
            placeholder="Optional note"
            className={`${FLD_TEXTAREA} !resize-none overflow-hidden !text-[0.90625rem] leading-[1.45] placeholder:text-[0.8125rem] placeholder:leading-[1.4]`}
            value={note}
            onChange={onNoteChange}
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.preventDefault();
            }}
          />
        </Field>
        {/* The chip differs from the card's label only by the note, so the
            line shows only while there is one. */}
        {previewNote !== '' && (
          <p className="mt-1 text-xs text-ink-soft" data-testid="lifecycle-preview">
            Shows as: <span className="font-medium text-sage-800">{previewLabel}</span>
          </p>
        )}
      </div>
      {showDeath && (
        <div className="mt-3 mb-1.5">
          <Field
            label="Date of death"
            htmlFor={DEATH_BOX_ID}
            help={deathShown === null && death === '' ? DEATH_DATE_LATER_SENTENCE : undefined}
            error={deathShown !== null ? DEATH_SENTENCES[deathShown] : undefined}
          >
            <DateInput
              ref={deathRef}
              id={DEATH_BOX_ID}
              data-testid="lifecycle-date-of-death"
              max={today}
              size="field"
              className="w-full max-w-[11rem]"
              value={death}
              aria-invalid={deathShown !== null ? 'true' : undefined}
              aria-describedby={
                deathShown !== null ? `${DEATH_BOX_ID}-error` : death === '' ? `${DEATH_BOX_ID}-help` : undefined
              }
              onChange={(e) => {
                setDeathShown(null);
                setDeath(e.target.value);
              }}
              onBlur={() => setDeathShown(deathProblem)}
            />
          </Field>
        </div>
      )}
      {showHospice && (
        <div className="mt-3 mb-1.5">
          <Field
            label="On hospice since"
            htmlFor={HOSPICE_BOX_ID}
            help="Leave empty when the client is not on hospice."
            error={hospiceFuture ? HOSPICE_FUTURE_SENTENCE : undefined}
          >
            <DateInput
              id={HOSPICE_BOX_ID}
              data-testid="lifecycle-hospice-since"
              max={today}
              size="field"
              className="w-full max-w-[11rem]"
              value={hospice}
              aria-invalid={hospiceFuture ? 'true' : undefined}
              aria-describedby={hospiceFuture ? `${HOSPICE_BOX_ID}-error` : `${HOSPICE_BOX_ID}-help`}
              onChange={(e) => {
                setHospiceFuture(false);
                setHospice(e.target.value);
              }}
            />
          </Field>
        </div>
      )}
      {setLifecycle.isError && !deathRefused && (
        <p role="alert" data-testid="lifecycle-error" className="mt-2 text-xs text-pepper">
          {pageWriteText(setLifecycle.error, contact)}
        </p>
      )}
    </Dialog>
  );
}

/** Engagement display labels. EXPORTED: the engagement PILL sits on the
 *  forest header band (ClientRecord.tsx), beside the lifecycle status
 *  pill; the Change service dialog keeps the only door to CHANGE it. */
export const ENGAGEMENT_LABELS: Record<ClientEngagement, string> = {
  care_management: 'Care management',
  spice: 'SPICE',
  both: 'Care management + SPICE',
};

// ---------------------------------------------------------------------
// SPICE schedule: the Edit dialog every schedule door opens.
// ---------------------------------------------------------------------

const SPICE_DAY_OPTIONS = [1, 2, 3, 4, 5, 6, 7].map((v) => ({ value: v, label: SPICE_DAY_NAMES[v]! }));
const SPICE_FREQUENCY_OPTIONS: { value: SpiceSessionFrequency; label: string }[] = (
  ['weekly', 'every_two_weeks', 'monthly'] as const
).map((value) => ({ value, label: SPICE_FREQUENCY_TITLES[value] }));
const SPICE_WEEK_OF_MONTH_OPTIONS: { value: SpiceWeekOfMonth; label: string }[] = (
  ['first', 'second', 'third', 'fourth', 'last'] as const
).map((value) => ({ value, label: SPICE_WEEK_OF_MONTH_LABELS[value] }));
/** A Friendly Visitor session's duration, in minutes. */
const SPICE_FV_MIN_MINUTES = 15;
const SPICE_FV_MAX_MINUTES = 480;
const SPICE_FV_DEFAULT_MINUTES = 60;
const SPICE_MAX_SESSIONS = 14;
const SPICE_STARTING_WEEKS = 8;
const SPICE_DEFAULT_TIME = '10:00';

/** The SPICE therapist select a door may add to the schedule editor (the
 *  SPICE week grid does): the client's CURRENT therapist and engagement,
 *  so a changed pick is written through setClientEngagement (the same
 *  write the record's Change service dialog uses) before the plan is
 *  saved. It is the window's way to change the client's own therapist; a
 *  session's own Therapist select is on every card, whichever door opened
 *  it. */
export interface SpiceScheduleTherapistContext {
  currentRosterId: string | null;
  currentEngagement: ClientEngagementValue;
  /** What the select opens on; defaults to the current therapist. The
   *  grid passes the row the door was opened from. */
  initialRosterId?: string | null;
}

export interface SpiceScheduleEditDialogProps {
  plan: SpiceScheduleVitals | null;
  onClose: () => void;
  clientId?: string;
  tenantSlug?: string;
  /** Named in the title when the door is not on the client's own record. */
  clientName?: string;
  /** Adds the plan-level SPICE therapist select (see
   *  SpiceScheduleTherapistContext). */
  therapist?: SpiceScheduleTherapistContext;
  /** The client's own SPICE therapist, for a door without the plan-level
   *  select: each session's Therapist select names her as its first
   *  option. null when the client has none yet. */
  spiceTherapistRosterId?: string | null;
  /** The therapist the first session added in this window takes (the
   *  grid's Add door on another therapist's row, for a client who keeps
   *  her own therapist). */
  initialSessionTherapistRosterId?: string | null;
  /** The weekday the first session added takes while the form has none
   *  (the grid column the door was opened from); Monday without it. */
  initialWeekday?: number;
  /** The date the door was opened from ('YYYY-MM-DD'): the grid cell's
   *  or pill's date, or My week's visit day. A session switched to
   *  Monthly takes its week of the month from its own weekday's date in
   *  this week; without it, from the next occurrence of that weekday on
   *  or after the practice day. */
  initialDate?: string;
}

/** One session row of the form. */
interface SessionDraft {
  key: number;
  kind: SpiceSessionKind;
  weekday: number;
  time: string;
  frequency: SpiceSessionFrequency;
  anchorWeek: string;
  /** The week of the month while Monthly; kept across a change of day. */
  weekOfMonth: SpiceWeekOfMonth;
  duration: string;
  /** The session's own therapist; '' is the client's own SPICE therapist. */
  therapistId: string;
}

/** 'YYYY-MM-DD' plus whole days (UTC arithmetic on a zone-less date). */
function addIsoDays(iso: string, days: number): string {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d) + days * 86_400_000).toISOString().slice(0, 10);
}

/** The Monday of the week containing `iso`. */
function mondayOf(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return addIsoDays(iso, -(dow === 0 ? 6 : dow - 1));
}

/** The weeks an every-two-weeks session may start: this week's Monday and
 *  the next seven, plus a stored anchor that is older. */
function startingWeekOptions(thisMonday: string, stored: readonly string[]): string[] {
  const weeks = Array.from({ length: SPICE_STARTING_WEEKS }, (_, i) => addIsoDays(thisMonday, i * 7));
  const older = stored.filter((w) => w !== '' && !weeks.includes(w));
  return [...new Set(older)].sort().concat(weeks);
}

function draftFromSession(key: number, s: SpiceScheduleVitals['sessions'][number], thisMonday: string): SessionDraft {
  return {
    key,
    kind: s.kind,
    weekday: s.weekday,
    time: s.startTime,
    frequency: s.frequency,
    anchorWeek: s.anchorWeek ?? thisMonday,
    weekOfMonth: s.weekOfMonth ?? 'first',
    duration: String(s.durationMinutes ?? SPICE_FV_DEFAULT_MINUTES),
    therapistId: s.therapistRosterId ?? '',
  };
}

/** The date a session on `weekday` takes its week of the month from when
 *  it is switched to Monthly: that weekday in the week of the door's date,
 *  or else the next such weekday on or after `today`. */
function monthlyAnchorDate(weekday: number, initialDate: string | undefined, today: string): string {
  if (initialDate !== undefined) return addIsoDays(mondayOf(initialDate), weekday - 1);
  const todayWeekday = ((new Date(`${today}T00:00:00Z`).getUTCDay() + 6) % 7) + 1;
  return addIsoDays(today, (weekday - todayWeekday + 7) % 7);
}

function durationIsValid(d: SessionDraft): boolean {
  if (d.kind !== 'spice_friendly_visitor') return true;
  const n = Number(d.duration);
  return Number.isInteger(n) && n >= SPICE_FV_MIN_MINUTES && n <= SPICE_FV_MAX_MINUTES;
}

/** The kind toggle's two segments, in the order they read. */
const KIND_OPTIONS: ReadonlyArray<{ value: SpiceSessionKind; testId: string }> = [
  { value: 'spice', testId: 'spice-session-kind-spice' },
  { value: 'spice_friendly_visitor', testId: 'spice-session-kind-fv' },
];

/** A row as the PUT sends it. The therapist key (an id, or null for the
 *  client's own) travels only when `keyed`: whenever the plan the window
 *  opened on or any row names a session therapist. */
function draftToSession(d: SessionDraft, keyed: boolean): SpiceScheduleSession {
  return {
    weekday: d.weekday,
    start_time: d.time,
    kind: d.kind,
    frequency: d.frequency,
    anchor_week: d.frequency === 'every_two_weeks' ? d.anchorWeek : null,
    week_of_month: d.frequency === 'monthly' ? d.weekOfMonth : null,
    duration_minutes: d.kind === 'spice_friendly_visitor' ? Number(d.duration) : null,
    ...(keyed ? { therapist_roster_id: d.therapistId === '' ? null : d.therapistId } : {}),
  };
}

function sameSession(a: SpiceScheduleSession, b: SpiceScheduleSession): boolean {
  return (
    a.weekday === b.weekday &&
    a.start_time === b.start_time &&
    a.kind === b.kind &&
    a.frequency === b.frequency &&
    a.anchor_week === b.anchor_week &&
    a.week_of_month === b.week_of_month &&
    a.duration_minutes === b.duration_minutes &&
    (a.therapist_roster_id ?? null) === (b.therapist_roster_id ?? null)
  );
}

/** Two PUT bodies that would save the same plan: the cover or pause as
 *  it stands and the sessions in order. */
function samePlanInput(a: SpiceVisitPlanInput, b: SpiceVisitPlanInput): boolean {
  return (
    a.status === b.status &&
    a.hold_reason === b.hold_reason &&
    a.hold_note === b.hold_note &&
    a.status_until === b.status_until &&
    a.cover_roster_id === b.cover_roster_id &&
    a.sessions.length === b.sessions.length &&
    a.sessions.every((x, i) => sameSession(x, b.sessions[i]!))
  );
}

/** The sentences a press finds a session row wanting. */
const SESSION_TIME_GAP = 'Enter a time for this session.';
const SESSION_DURATION_GAP = `Enter a length from ${SPICE_FV_MIN_MINUTES} to ${SPICE_FV_MAX_MINUTES} minutes.`;
const SESSION_CLASH = 'Two sessions are on the same day at the same time. Change one, then save.';

/**
 * SpiceScheduleEditDialog: the schedule as a list of sessions. Each
 * session names its kind (SPICE or Friendly Visitor), day, time and
 * frequency (Weekly, Every two weeks or Monthly); an every-two-weeks
 * session names the week it starts, a monthly session its week of the
 * month (Which week), and a Friendly Visitor session its duration. Each
 * session also names its Therapist, on a line of its own under those
 * boxes: the first option is the client's own SPICE therapist (the
 * default, sent as null), then every other SPICE therapist A to Z. A
 * client with no schedule opens with none, and "+ Add a session" adds
 * one. Two sessions that can fall on the same day at the same time (a
 * weekly or every-two-weeks session beside any other, two monthly ones
 * in the same week, or a fourth beside a last) are marked, each with the
 * clash line under its boxes. "Save schedule" is disabled only while
 * sending: pressed with nothing changed it closes and sends nothing;
 * pressed with a row that has no time, a Friendly Visitor length out of
 * range, a clash, or no session at all, it sends nothing, says so under
 * the box (or under "+ Add a session") and moves focus to the first gap.
 * A failed save or removal shows the house sentence for it. The summary
 * at the top names exactly the sessions Save sends, each session's
 * therapist included. The PUT names therapist_roster_id on every session
 * whenever the plan or any row names one, and on none otherwise.
 * The plan's cover or pause is the SPICE tab's; a save here carries it
 * over as it stands, except that choosing the plan's covering therapist
 * as the client's therapist sends the plan regular (a cover ends when
 * the client's own therapist becomes the cover), on the first Save and
 * on any retry.
 *
 * From a grid door the window also carries the plan-level "SPICE
 * therapist" select: it changes the client's own therapist (the
 * engagement write, first), and every session set to the default
 * follows; a session set to another therapist stays with her. Each
 * session's default option follows that select, and a session naming
 * the therapist just chosen there reads the default again.
 *
 * A client with a schedule on file can lose it here: "Remove schedule"
 * asks first, then removes the schedule whole (a DELETE), and the client
 * moves to the grid's Not scheduled yet.
 *
 * Every door onto a client's schedule opens this one editor: the
 * record's SPICE tab, My week's per-line "Edit schedule" link and the
 * capacity map's SPICE Assign (both through
 * src/components/home/MyWeekEditScheduleDialog.tsx, which fetches the
 * plan), and the SPICE week grid's cell door. The editor answers to
 * canEditClientSchedule itself and renders nothing for a viewer who may
 * not change a client's schedule, whichever door opened it. A caller
 * passes clientId/tenantSlug explicitly; without them the editor reads
 * both from the route.
 */
export function SpiceScheduleEditDialog(props: SpiceScheduleEditDialogProps): ReactNode {
  const { whoami } = useTenant();
  if (!canEditClientSchedule(whoami)) return null;
  return <SpiceScheduleEditForm {...props} />;
}

function SpiceScheduleEditForm({
  plan,
  onClose,
  clientId: clientIdProp,
  tenantSlug: tenantSlugProp,
  clientName,
  therapist,
  spiceTherapistRosterId = null,
  initialSessionTherapistRosterId = null,
  initialWeekday,
  initialDate,
}: SpiceScheduleEditDialogProps): ReactNode {
  const params = useParams<{ tenantSlug: string; clientId: string }>();
  const tenantSlug = tenantSlugProp ?? params.tenantSlug ?? '';
  const clientId = clientIdProp ?? params.clientId ?? '';
  const { spiceTherapists } = useAssignOptions(tenantSlug);
  const setPlan = useSetClientSpicePlan(tenantSlug);
  const deletePlan = useDeleteClientSpicePlan(tenantSlug);
  const setEngagement = useSetClientEngagement(tenantSlug);
  const kindColors = useSpiceKindColors();

  // The plan-level SPICE therapist select (grid doors only): opens on the
  // row the door was opened from, else the client's current therapist.
  // '' = leave as is.
  const [therapistId, setTherapistId] = useState(therapist?.initialRosterId ?? therapist?.currentRosterId ?? '');
  const therapistChanged = therapist !== undefined && therapistId !== '' && therapistId !== (therapist.currentRosterId ?? '');
  // The client's own therapist as the window stands: the plan-level
  // choice when a grid door shows it, else what the door passed in. Each
  // session's default option names her, and the other options leave her
  // out.
  const leadRosterId: string | null =
    therapist !== undefined ? (therapistId !== '' ? therapistId : therapist.currentRosterId) : spiceTherapistRosterId;
  const leadName = leadRosterId === null ? null : (spiceTherapists.find((t) => t.roster_id === leadRosterId)?.name ?? null);
  const sessionTherapistOptions = spiceTherapists
    .filter((t) => t.roster_id !== leadRosterId)
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name));
  const leadOptionText =
    leadRosterId === null ? "The client's SPICE therapist (none yet)" : `${leadName ?? "The client's SPICE therapist"} (client's therapist)`;

  /** The plan-level select changed: a session naming the therapist now
   *  chosen reads the default again (she is the client's own now). */
  function changeLead(value: string): void {
    setTherapistId(value);
    const lead = value !== '' ? value : (therapist?.currentRosterId ?? null);
    if (lead === null) return;
    setDrafts((prev) => prev.map((d) => (d.therapistId === lead ? { ...d, therapistId: '' } : d)));
  }

  const [today] = useState(() => practiceTodayIso());
  const thisMonday = mondayOf(today);
  // A client with no schedule opens with no sessions: nothing saves
  // until one is added.
  const [nextKey, setNextKey] = useState(() => plan?.sessions.length ?? 0);
  const [drafts, setDrafts] = useState<SessionDraft[]>(() =>
    (plan?.sessions ?? []).map((s, i) => draftFromSession(i, s, thisMonday)),
  );
  // The therapist the first session added here takes (the grid's Add door
  // on another therapist's row), used once.
  const [pendingSessionTherapist, setPendingSessionTherapist] = useState(initialSessionTherapistRosterId ?? '');
  // Whether the plan the window opened on names a session therapist: the
  // PUT then names the key on every session, as it does once any row
  // names one.
  const planNamesTherapist = (plan?.sessions ?? []).some((s) => s.therapistRosterId !== undefined);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  const contact = useSupportContact();
  // The gaps the last press found, by row key; each clears on the next
  // change to its own box, the no-session line when a session is added.
  const [timeGaps, setTimeGaps] = useState<ReadonlySet<number>>(() => new Set());
  const [durationGaps, setDurationGaps] = useState<ReadonlySet<number>>(() => new Set());
  const [noneGap, setNoneGap] = useState(false);
  // The boxes a press may move focus to, by element id.
  const fields = useRef(new Map<string, HTMLElement | null>());
  function fieldRef(id: string): (el: HTMLElement | null) => void {
    return (el) => {
      fields.current.set(id, el);
    };
  }
  // The therapist an earlier Save already wrote, so a retry after a failed
  // plan save re-fires only the plan.
  const [engagementSavedFor, setEngagementSavedFor] = useState<string | null>(null);

  const storedAnchors = (plan?.sessions ?? []).map((s) => s.anchorWeek ?? '');
  const weekOptions = startingWeekOptions(thisMonday, storedAnchors);

  // Two rows clash when they can fall on the same date at the same time.
  const duplicateKeys = new Set<number>();
  const timed = drafts.filter((d) => d.time !== '');
  for (let i = 0; i < timed.length; i++) {
    for (let j = i + 1; j < timed.length; j++) {
      if (spiceSessionsClash(draftToSession(timed[i]!, false), draftToSession(timed[j]!, false))) {
        duplicateKeys.add(timed[i]!.key);
        duplicateKeys.add(timed[j]!.key);
      }
    }
  }
  // The sessions as they will save: the PUT sends exactly these, and the
  // summary names exactly these, each session's therapist by name. A row
  // without a time is not a session yet; a press says so under its Time.
  const keyed = planNamesTherapist || drafts.some((d) => d.therapistId !== '');
  const sessions = timed.map((d) => draftToSession(d, keyed));
  const summarySessions = sessions.map((s) => {
    const name = spiceTherapists.find((t) => t.roster_id === (s.therapist_roster_id ?? null))?.name;
    return name === undefined ? s : { ...s, therapist_name: name };
  });

  const saving = setPlan.isPending || setEngagement.isPending || deletePlan.isPending;
  // Only a schedule on file can be removed; a client with none yet has
  // nothing to lose here.
  const hasPlan = plan !== null;

  function update(key: number, patch: Partial<SessionDraft>): void {
    setDrafts((prev) => prev.map((d) => (d.key === key ? { ...d, ...patch } : d)));
    if ('time' in patch) setTimeGaps((prev) => without(prev, key));
    if ('duration' in patch) setDurationGaps((prev) => without(prev, key));
  }

  function without(set: ReadonlySet<number>, key: number): ReadonlySet<number> {
    if (!set.has(key)) return set;
    const next = new Set(set);
    next.delete(key);
    return next;
  }

  /** A change of frequency. Switched to Monthly, the session's week comes
   *  from a date (monthlyAnchorDate); a fifth occurrence reads Last. */
  function changeFrequency(d: SessionDraft, frequency: SpiceSessionFrequency): void {
    if (frequency === 'monthly' && d.frequency !== 'monthly') {
      const date = monthlyAnchorDate(d.weekday, initialDate, today);
      update(d.key, { frequency, weekOfMonth: weekOfMonthForDrop(date, null) });
      return;
    }
    update(d.key, { frequency });
  }

  // Each session row's two kind radios, in KIND_OPTIONS order, for the
  // arrow keys to move the focus with the check.
  const kindRadios = useRef(new Map<number, Array<HTMLButtonElement | null>>());
  function kindRadioRef(key: number): Array<HTMLButtonElement | null> {
    let radios = kindRadios.current.get(key);
    if (!radios) {
      radios = [];
      kindRadios.current.set(key, radios);
    }
    return radios;
  }

  /** The kind toggle's arrow keys: Right and Down check the next kind,
   *  Left and Up the previous, both wrapping, and the focus follows the
   *  check. Space and Enter are the radio's own click. */
  function onKindKey(e: KeyboardEvent<HTMLButtonElement>, key: number, index: number): void {
    const step = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 0;
    if (step === 0) return;
    e.preventDefault();
    const next = (index + step + KIND_OPTIONS.length) % KIND_OPTIONS.length;
    update(key, { kind: KIND_OPTIONS[next]!.value });
    kindRadioRef(key)[next]?.focus();
  }

  /** A weekly SPICE session: the first on the day the door names (Monday
   *  without one) at 10:00, each later one on the day after the last,
   *  at its time; the first one added takes the therapist the door
   *  named, if any, and every one after it the client's own. */
  function addSession(): void {
    setNoneGap(false);
    const last = drafts[drafts.length - 1];
    const therapistIdForNew = pendingSessionTherapist !== '' && pendingSessionTherapist !== leadRosterId ? pendingSessionTherapist : '';
    setPendingSessionTherapist('');
    setDrafts((prev) => [
      ...prev,
      {
        key: nextKey,
        kind: 'spice',
        weekday: last ? (last.weekday % 7) + 1 : (initialWeekday ?? 1),
        time: last?.time || SPICE_DEFAULT_TIME,
        frequency: 'weekly',
        anchorWeek: thisMonday,
        weekOfMonth: 'first',
        duration: String(SPICE_FV_DEFAULT_MINUTES),
        therapistId: therapistIdForNew,
      },
    ]);
    setNextKey((k) => k + 1);
  }

  function removeSession(key: number): void {
    setDrafts((prev) => prev.filter((d) => d.key !== key));
  }

  /** The form as a PUT body: the sessions as edited, the plan's cover or
   *  pause as it stands. When the plan is covered and its cover is the
   *  therapist the dialog names (chosen now or already), the plan goes
   *  regular: the engagement write ends that cover on the server, so the
   *  first Save, and a retry after it stood, send a body the server
   *  accepts. */
  function planInput(): SpiceVisitPlanInput {
    const REGULAR = { status: 'active' as const, hold_reason: null, hold_note: null, status_until: null, cover_roster_id: null };
    const base: SpiceVisitPlanInput = plan !== null ? spiceVitalsToInput(plan, today) : { ...REGULAR, sessions: [] };
    const coverIsTherapist = base.status === 'covered' && therapistId !== '' && base.cover_roster_id === therapistId;
    return coverIsTherapist ? { ...REGULAR, sessions } : { ...base, sessions };
  }

  async function save(): Promise<void> {
    setSaveError(null);
    try {
      // A changed therapist is written FIRST, through the same engagement
      // write the record's Change service dialog uses (nextSpiceEngagement
      // keeps a both-services client on both), so the plan that follows
      // lands on the new therapist's row of the grid. Two requests, stated: if the
      // plan save fails after the move, the move stands and Save re-fires
      // only the plan.
      if (therapistChanged && therapist && engagementSavedFor !== therapistId) {
        await setEngagement.mutateAsync({
          id: clientId,
          engagement: nextSpiceEngagement(therapist.currentEngagement),
          spiceTherapistRosterId: therapistId,
        });
        setEngagementSavedFor(therapistId);
      }
      await setPlan.mutateAsync({ clientId, input: planInput() });
      onClose();
    } catch (err) {
      setSaveError(pageWriteText(err, contact));
    }
  }

  /** Nothing to send: no therapist change, every row timed, and the plan
   *  the PUT would carry is the plan as it stands (a client with no plan:
   *  no row at all). */
  function nothingChanged(): boolean {
    if (therapistChanged) return false;
    if (drafts.some((d) => d.time === '')) return false;
    if (plan === null) return drafts.length === 0;
    return samePlanInput(planInput(), spiceVitalsToInput(plan, today));
  }

  /** "Save schedule": nothing changed closes; a gap or a clash says so
   *  and moves focus to the first, in the dialog's order; otherwise it
   *  saves. */
  function pressSave(): void {
    if (nothingChanged()) {
      onClose();
      return;
    }
    const times = new Set<number>();
    const durations = new Set<number>();
    let first: string | null = null;
    for (const d of drafts) {
      const id = `spice-session-${d.key}`;
      if (duplicateKeys.has(d.key)) first ??= `${id}-day`;
      if (d.time === '') {
        times.add(d.key);
        first ??= `${id}-time`;
      }
      if (!durationIsValid(d)) {
        durations.add(d.key);
        first ??= `${id}-duration`;
      }
    }
    const none = drafts.length === 0;
    if (none) first ??= 'spice-session-add';
    setTimeGaps(times);
    setDurationGaps(durations);
    setNoneGap(none);
    if (first !== null) {
      fields.current.get(first)?.focus();
      return;
    }
    void save();
  }

  async function removeSchedule(): Promise<void> {
    setSaveError(null);
    try {
      await deletePlan.mutateAsync({ clientId });
      onClose();
    } catch (err) {
      setSaveError(pageWriteText(err, contact));
    }
  }

  return (
    <Dialog
      title={clientName ? `Edit SPICE schedule: ${clientName}` : 'Edit SPICE schedule'}
      size="xl"
      testId="spice-schedule-edit-dialog"
      onClose={onClose}
      footer={
        <>
          {hasPlan && (
            <Button
              variant="ghost"
              size="sm"
              className="mr-auto text-pepper"
              data-testid="spice-schedule-remove"
              disabled={saving || confirmingRemove}
              onClick={() => setConfirmingRemove(true)}
            >
              Remove schedule
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={saving}
            loading={setPlan.isPending || setEngagement.isPending}
            data-testid="spice-schedule-save"
            onClick={pressSave}
          >
            Save schedule
          </Button>
        </>
      }
    >
      <div className="space-y-4 py-2">
        <SpiceScheduleSummary sessions={summarySessions} className="text-sm text-ink-soft" />

        {therapist !== undefined && (
          <Field label="SPICE therapist" htmlFor="spice-therapist-select">
            <SelectField
              size="field"
              id="spice-therapist-select"
              data-testid="spice-schedule-therapist-select"
              value={therapistId}
              onChange={(e) => changeLead(e.target.value)}
            >
              <option value="">{therapist.currentRosterId ? 'Keep the current therapist' : 'No therapist yet'}</option>
              {spiceTherapists.map((t) => (
                <option key={t.roster_id} value={t.roster_id}>
                  {t.name}
                </option>
              ))}
            </SelectField>
            {therapistChanged && (
              <span className="mt-1 block text-xs text-ink-soft" data-testid="spice-schedule-therapist-note">
                Saving makes this the client's SPICE therapist. Sessions set to another therapist stay with that therapist.
              </span>
            )}
          </Field>
        )}

        <ul className="space-y-3">
          {drafts.map((d) => {
            const id = `spice-session-${d.key}`;
            const isFv = d.kind === 'spice_friendly_visitor';
            const isDuplicate = duplicateKeys.has(d.key);
            const clashId = `${id}-clash`;
            const timeGap = timeGaps.has(d.key);
            const durationGap = durationGaps.has(d.key);
            const describedBy = (...ids: Array<string | false>): string | undefined =>
              ids.filter((x): x is string => x !== false).join(' ') || undefined;
            return (
              <li
                key={d.key}
                data-testid="spice-session-row"
                aria-invalid={isDuplicate || undefined}
                className={`rounded-tight border p-3 ${isDuplicate ? 'border-pepper/60 bg-pepper/[6%]' : 'border-sage-800/[14%]'}`}
              >
                <div className="mb-2 flex flex-wrap items-center gap-2">
                  <div
                    role="radiogroup"
                    aria-label="Kind"
                    data-testid="spice-session-kind"
                    className="inline-flex rounded-pill border border-sage-300 bg-sand p-0.5"
                  >
                    {KIND_OPTIONS.map((opt, i) => {
                      const checked = d.kind === opt.value;
                      return (
                        <Button
                          key={opt.value}
                          variant="ghost"
                          size="sm"
                          role="radio"
                          aria-checked={checked}
                          tabIndex={checked ? 0 : -1}
                          data-testid={opt.testId}
                          ref={(el) => {
                            kindRadioRef(d.key)[i] = el;
                          }}
                          className={`!rounded-pill ${checked ? 'shadow-sh1' : 'text-sage-800'}`}
                          style={
                            checked
                              ? { backgroundColor: kindColors[opt.value].fill, color: kindColors[opt.value].text }
                              : undefined
                          }
                          onClick={() => update(d.key, { kind: opt.value })}
                          onKeyDown={(e) => onKindKey(e, d.key, i)}
                        >
                          {!checked && <SpiceKindDot kind={opt.value} />}
                          {SPICE_KIND_LABELS[opt.value]}
                        </Button>
                      );
                    })}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="ml-auto text-pepper"
                    data-testid="spice-session-remove"
                    aria-label={`Remove session, ${SPICE_DAY_NAMES[d.weekday] ?? ''}`}
                    onClick={() => removeSession(d.key)}
                  >
                    Remove session
                  </Button>
                </div>
                <div data-testid="spice-session-fields" className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <Field label="Day" htmlFor={`${id}-day`}>
                    <SelectField
                      size="field"
                      ref={fieldRef(`${id}-day`)}
                      id={`${id}-day`}
                      data-testid="spice-session-day"
                      value={d.weekday}
                      aria-describedby={describedBy(isDuplicate && clashId)}
                      onChange={(e) => update(d.key, { weekday: Number(e.target.value) })}
                    >
                      {SPICE_DAY_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </SelectField>
                  </Field>
                  <Field label="Time" htmlFor={`${id}-time`} error={timeGap ? SESSION_TIME_GAP : undefined}>
                    <input
                      ref={fieldRef(`${id}-time`)}
                      id={`${id}-time`}
                      data-testid="spice-session-time"
                      type="time"
                      className={FLD}
                      value={d.time}
                      aria-invalid={timeGap ? 'true' : undefined}
                      aria-describedby={describedBy(timeGap && `${id}-time-error`, isDuplicate && clashId)}
                      onChange={(e) => update(d.key, { time: e.target.value })}
                    />
                  </Field>
                  <Field label="Frequency" htmlFor={`${id}-frequency`}>
                    <SelectField
                      size="field"
                      id={`${id}-frequency`}
                      data-testid="spice-session-frequency"
                      value={d.frequency}
                      onChange={(e) => changeFrequency(d, e.target.value as SpiceSessionFrequency)}
                    >
                      {SPICE_FREQUENCY_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </SelectField>
                  </Field>
                  {d.frequency === 'monthly' && (
                    <Field label="Which week" htmlFor={`${id}-week-of-month`}>
                      <SelectField
                        size="field"
                        id={`${id}-week-of-month`}
                        data-testid="spice-session-week-of-month"
                        value={d.weekOfMonth}
                        onChange={(e) => update(d.key, { weekOfMonth: e.target.value as SpiceWeekOfMonth })}
                      >
                        {SPICE_WEEK_OF_MONTH_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </SelectField>
                    </Field>
                  )}
                  {d.frequency === 'every_two_weeks' && (
                    <Field label="Starting" htmlFor={`${id}-starting`}>
                      <SelectField
                        size="field"
                        id={`${id}-starting`}
                        data-testid="spice-session-starting"
                        value={d.anchorWeek}
                        onChange={(e) => update(d.key, { anchorWeek: e.target.value })}
                      >
                        {weekOptions.map((w) => (
                          <option key={w} value={w}>
                            Week of {formatSpiceShortDate(w)}
                          </option>
                        ))}
                      </SelectField>
                    </Field>
                  )}
                  {isFv && (
                    <Field
                      label="Duration (min)"
                      htmlFor={`${id}-duration`}
                      error={durationGap ? SESSION_DURATION_GAP : undefined}
                    >
                      <input
                        ref={fieldRef(`${id}-duration`)}
                        id={`${id}-duration`}
                        data-testid="spice-session-duration"
                        type="number"
                        min={SPICE_FV_MIN_MINUTES}
                        max={SPICE_FV_MAX_MINUTES}
                        className={FLD}
                        value={d.duration}
                        aria-invalid={durationGap ? 'true' : undefined}
                        aria-describedby={describedBy(durationGap && `${id}-duration-error`)}
                        onChange={(e) => update(d.key, { duration: e.target.value })}
                      />
                    </Field>
                  )}
                </div>
                <div className="mt-3">
                  <Field label="Therapist" htmlFor={`${id}-therapist`}>
                    <SelectField
                      size="field"
                      id={`${id}-therapist`}
                      data-testid="spice-session-therapist"
                      value={d.therapistId}
                      onChange={(e) => update(d.key, { therapistId: e.target.value })}
                    >
                      <option value="">{leadOptionText}</option>
                      {sessionTherapistOptions.map((t) => (
                        <option key={t.roster_id} value={t.roster_id}>
                          {t.name}
                        </option>
                      ))}
                    </SelectField>
                  </Field>
                </div>
                {isDuplicate && (
                  <p id={clashId} role="alert" data-testid="spice-session-duplicate" className="mt-2 text-xs text-pepper">
                    {SESSION_CLASH}
                  </p>
                )}
              </li>
            );
          })}
        </ul>

        <Button
          ref={fieldRef('spice-session-add')}
          variant="ghost"
          size="sm"
          data-testid="spice-session-add"
          disabled={drafts.length >= SPICE_MAX_SESSIONS}
          onClick={addSession}
        >
          + Add a session
        </Button>
        {noneGap && (
          <p role="alert" data-testid="spice-session-none" className="text-xs text-pepper">
            {hasPlan ? 'Add a session, or use Remove schedule to take the schedule away.' : 'Add a session before saving.'}
          </p>
        )}

        {confirmingRemove && (
          <div
            data-testid="spice-schedule-remove-line"
            className="flex flex-wrap items-center gap-2 rounded-tight border border-pepper/30 bg-pepper/[6%] px-3 py-2 text-sm text-sage-800"
          >
            <span className="mr-auto">
              Remove {clientName ?? 'this client'}'s SPICE schedule? They move to Not scheduled yet.
            </span>
            <Button
              variant="danger"
              size="sm"
              data-testid="spice-schedule-remove-confirm"
              loading={deletePlan.isPending}
              disabled={saving}
              onClick={() => void removeSchedule()}
            >
              Remove
            </Button>
            <Button
              variant="ghost"
              size="sm"
              data-testid="spice-schedule-remove-keep"
              disabled={deletePlan.isPending}
              onClick={() => setConfirmingRemove(false)}
            >
              Keep
            </Button>
          </div>
        )}

        {saveError !== null && (
          <div role="alert" data-testid="spice-schedule-error" className="text-xs text-pepper">
            {saveError}
          </div>
        )}
      </div>
    </Dialog>
  );
}

/** The language select's value for "Other": a typed language in the box
 *  under it. Never a stored value. */
export const LANGUAGE_OTHER = '__other__';
/** The language the picker shows on a client with no language yet (LP-33).
 *  Shown, never written until the person saves the form. */
export const DEFAULT_LANGUAGE = 'English';

/** The edit form's three groups, in the ruled order (LP-33). The band's
 *  About row shows its facts in the same order. */
export const ABOUT_GROUPS = ['About them', 'Their care', 'Consent'] as const;

const ABOUT_CONSENT_LABELS: Record<TransportationConsent, string> = { consented: 'Consented', declined: 'Declined' };
const ABOUT_CONSENT_OPTIONS: readonly [SegmentedOption<TransportationConsent>, SegmentedOption<TransportationConsent>] = [
  { value: 'consented', label: ABOUT_CONSENT_LABELS.consented, suffix: 'consented' },
  { value: 'declined', label: ABOUT_CONSENT_LABELS.declined, suffix: 'declined' },
];
const ABOUT_CONSENT_ID = 'demographics-transportation-consent';
const ABOUT_CONSENT_SPEC: Readonly<Record<string, PayloadFieldSpec>> = {
  transportation_consent: { kind: 'enum', values: TRANSPORTATION_CONSENT_VALUES },
};
const PLACE_HELP = 'Pick a place from the Directory, or type its name.';

/** A place as the form holds it: the text in the box, and the Directory
 *  link while the text is a pick. */
interface PlaceDraft {
  text: string;
  link: string | null;
}

function placeDraftOf(place: ClientPlace | null): PlaceDraft {
  return { text: place?.name ?? '', link: place?.orgId ?? null };
}

function placeChanged(draft: PlaceDraft, stored: ClientPlace | null): boolean {
  return draft.text.trim() !== (stored?.name ?? '') || draft.link !== (stored?.orgId ?? null);
}

/** The two keys a place sends: the link with the name null, or the typed
 *  name (blank clears to null) with the link null. */
function placeFields(draft: PlaceDraft, linkKey: string, nameKey: string): Record<string, string | null> {
  if (draft.link !== null) return { [linkKey]: draft.link, [nameKey]: null };
  const name = draft.text.trim();
  return { [linkKey]: null, [nameKey]: name === '' ? null : name };
}

/** A quiet group inside the edit form: a small title over its fields. */
function AboutGroup({ title, children }: { title: (typeof ABOUT_GROUPS)[number]; children: ReactNode }): ReactNode {
  return (
    <fieldset className="m-0 flex min-w-0 flex-col gap-3 border-0 p-0" data-testid="about-edit-group">
      <legend className="mb-1 text-title-sm font-semibold text-sage-800">{title}</legend>
      <div className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2">{children}</div>
    </fieldset>
  );
}

/**
 * AboutEditDialog: the record's one Edit for the About facts, in three
 * quiet groups (LP-33, Amanda's order): "About them" (date of birth,
 * gender, primary language, relationship status), "Their care" (service
 * start, preferred hospital, pharmacy) and "Consent" (transportation
 * consent). The date of death is not here (LP-25): Change client status is
 * the one form that edits it. The service and its leads have their own
 * dialog, Change service, and the care manager's own door is
 * CriticalStrip's Assign/Change.
 *
 * Who sees what: the date of birth, gender, primary language, service
 * start and the two places are the office's (manage_client_identity);
 * relationship status is every seat's that opens the dialog; consent is a
 * seat that keeps the record and is not SPICE-only (canWriteClinicalRecord,
 * the strip's own door's gate and the server's record-keeper rule). A
 * field not drawn keeps its stored value, so it never reads as changed and
 * is never sent. A group with no field drawn is not drawn.
 *
 * One save: the changed demographics travel in one POST ('' clears to
 * null), then a changed consent goes through the strip's own request,
 * patchTransportationConsent, never a second write path.
 *
 * Primary language: a select over the tenant's list (features.languages,
 * the house fallback) plus Other, which reveals a text box. On a client
 * with no language the select shows English, so a save writes "English"
 * and Cancel writes nothing; a stored language off the list shows as
 * Other with its text.
 *
 * The date box stops at the practice day and shows the client's age
 * under it. A date later than today shows the future sentence under the
 * box on blur and on press, where nothing is sent and focus moves to the
 * date; only a changed date is judged, so a stored future date (an
 * imported client) never blocks a gender-only save and is not sent. The
 * sentence is judged against the practice day, the day the server judges
 * against, not the device's day. "Save changes" is pressable at rest;
 * pressed unchanged it closes and sends nothing.
 */
export function AboutEditDialog({
  vitals,
  onClose,
}: {
  vitals: VitalsBandData;
  onClose: () => void;
}): ReactNode {
  const { tenantSlug = '', clientId = '' } = useParams<{ tenantSlug: string; clientId: string }>();
  const setDemographics = useSetClientDemographics(tenantSlug);
  const queryClient = useQueryClient();
  const contact = useSupportContact();
  const [today] = useState(() => practiceTodayIso());
  const { whoami } = useTenant();
  const office = canManageClientIdentity(whoami);
  // Consent: the strip's door's gate, so the same seats may record it here
  // (a SPICE-only therapist reads it; the server refuses her the write).
  const clinical = canWriteClinicalRecord(whoami) && !isSpiceOnlyViewer(whoami);
  const tenantConfig = useTenantConfig(tenantSlug, { enabled: office && tenantSlug !== '' });
  const languages = readLanguages(tenantConfig.data);

  const [dob, setDob] = useState(vitals.dob ?? '');
  const [gender, setGender] = useState(vitals.gender ?? '');
  // The future sentence, shown from blur or a press until the next change.
  const [dobFuture, setDobFuture] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const dobRef = useRef<HTMLInputElement | null>(null);
  const [serviceStart, setServiceStart] = useState(vitals.serviceStartDate ?? '');
  const [relationship, setRelationship] = useState(vitals.relationshipStatus ?? '');
  // LP-3: a service start may be a day ahead; only a date that is not a
  // real calendar day is refused, and only the server says so.
  const [startProblem, setStartProblem] = useState<string | null>(null);
  const startRef = useRef<HTMLInputElement | null>(null);

  // Primary language: `pick` is null until the person touches the select,
  // so the shown value follows the list as it loads: the stored language
  // when it is on the list, Other with its text when not, English on a
  // client with none (when the list carries it).
  const storedLanguage = vitals.primaryLanguage ?? '';
  const [languagePick, setLanguagePick] = useState<string | null>(null);
  const [languageOther, setLanguageOther] = useState(() =>
    storedLanguage !== '' && !languages.some((l) => l.toLowerCase() === storedLanguage.toLowerCase()) ? storedLanguage : '',
  );
  const listed = (value: string): string | undefined => languages.find((l) => l.toLowerCase() === value.toLowerCase());
  const languageShown =
    languagePick ??
    (storedLanguage === '' ? (listed(DEFAULT_LANGUAGE) ?? '') : (listed(storedLanguage) ?? LANGUAGE_OTHER));
  const languageResolved = languageShown === LANGUAGE_OTHER ? languageOther.trim() : languageShown;

  const [hospital, setHospital] = useState<PlaceDraft>(() => placeDraftOf(vitals.preferredHospital));
  const [pharmacy, setPharmacy] = useState<PlaceDraft>(() => placeDraftOf(vitals.pharmacy));

  const storedConsent = vitals.transportationConsent?.value ?? null;
  const [consent, setConsent] = useState<TransportationConsent | null>(storedConsent);
  const [consentError, setConsentError] = useState<string | null>(null);
  const [consentPending, setConsentPending] = useState(false);

  const dobChanged = dob !== (vitals.dob ?? '');
  const genderChanged = gender !== (vitals.gender ?? '');
  const dobIsFuture = dobChanged && dob !== '' && dob > today;
  const futureSentence = "Date of birth can't be in the future. Enter today or an earlier day.";
  const age = dobIsFuture ? null : ageLine(dob, today);

  // The CareTree fields (0221). Each date is judged only when changed, so
  // a stored out-of-range value (an imported client) never blocks a save
  // of another field and is not sent.
  const startChanged = serviceStart !== (vitals.serviceStartDate ?? '');
  const relationshipChanged = relationship !== (vitals.relationshipStatus ?? '');
  // LP-33: the office fields read as changed only when drawn.
  const languageChanged = office && languageResolved !== storedLanguage;
  const hospitalChanged = office && placeChanged(hospital, vitals.preferredHospital);
  const pharmacyChanged = office && placeChanged(pharmacy, vitals.pharmacy);
  const consentChanged = clinical && consent !== null && consent !== storedConsent;
  const anyChanged =
    dobChanged ||
    genderChanged ||
    startChanged ||
    relationshipChanged ||
    languageChanged ||
    hospitalChanged ||
    pharmacyChanged ||
    consentChanged;
  const pending = setDemographics.isPending || consentPending;

  // Only changed fields travel ('' clears to null, not recorded).
  function buildDemographicsFields(): Record<string, string | null> {
    const out: Record<string, string | null> = {};
    if (dobChanged) out.dob = dob === '' ? null : dob;
    if (genderChanged) out.gender = gender === '' ? null : gender;
    if (languageChanged) out.primary_language = languageResolved === '' ? null : languageResolved;
    if (relationshipChanged) out.relationship_status = relationship === '' ? null : relationship;
    if (startChanged) out.service_start_date = serviceStart === '' ? null : serviceStart;
    if (hospitalChanged) Object.assign(out, placeFields(hospital, 'preferred_hospital_org_id', 'preferred_hospital_name'));
    if (pharmacyChanged) Object.assign(out, placeFields(pharmacy, 'pharmacy_org_id', 'pharmacy_name'));
    return out;
  }

  /** "Save changes": nothing changed closes; a date out of range says so
   *  and moves focus to it; otherwise it saves the demographics, then a
   *  changed consent through the strip's own request. */
  async function pressSave(): Promise<void> {
    if (pending) return;
    if (!anyChanged) {
      onClose();
      return;
    }
    if (dobIsFuture) {
      setDobFuture(true);
      dobRef.current?.focus();
      return;
    }
    setSaveError(null);
    setConsentError(null);
    const fields = buildDemographicsFields();
    if (Object.keys(fields).length > 0) {
      try {
        await setDemographics.mutateAsync({ id: clientId, fields });
      } catch (err) {
        if (errorCodeOf(err) === 'invalid_value' && fieldOf(err) === 'dob') {
          setDobFuture(true);
          dobRef.current?.focus();
          return;
        }
        if (errorCodeOf(err) === 'invalid_value' && fieldOf(err) === 'service_start_date') {
          setStartProblem(fieldSentence('Service start', { kind: 'date' }, serviceStart));
          startRef.current?.focus();
          return;
        }
        setSaveError(pageWriteText(err, contact));
        return;
      }
    }
    if (consentChanged && consent !== null) {
      setConsentPending(true);
      try {
        await patchTransportationConsent(tenantSlug, clientId, consent);
        // ONE audited re-read of the aggregate after the write, as the
        // strip's own dialog does.
        await queryClient.invalidateQueries({ queryKey: ['clientRecord'] });
      } catch (err) {
        setConsentError(
          recordEditErrorMessage(
            err,
            {
              specs: ABOUT_CONSENT_SPEC,
              sent: { transportation_consent: consent },
              labels: { transportation_consent: 'Transportation consent' },
              valueLabels: { transportation_consent: ABOUT_CONSENT_LABELS },
            },
            contact,
          ),
        );
        return;
      } finally {
        setConsentPending(false);
      }
    }
    onClose();
  }

  const consentAria = fieldBoxAria(ABOUT_CONSENT_ID, {});
  const languageId = 'demographics-language';
  const languageOtherId = 'demographics-language-other';
  const hospitalId = 'demographics-preferred-hospital';
  const pharmacyId = 'demographics-pharmacy';

  return (
    <Dialog
      title="Edit about"
      size="md"
      testId="about-edit-dialog"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={pending}
            loading={pending}
            data-testid="about-edit-save"
            onClick={() => void pressSave()}
          >
            Save changes
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4 py-2">
        <AboutGroup title="About them">
          {office && (
            <Field
              label="Date of birth"
              htmlFor="demographics-dob"
              help={dobFuture ? undefined : (age ?? undefined)}
              error={dobFuture ? futureSentence : undefined}
            >
              <DateInput
                ref={dobRef}
                id="demographics-dob"
                data-testid="demographics-dob-input"
                max={today}
                size="field" className="w-full max-w-[11rem]"
                value={dob}
                aria-invalid={dobFuture ? 'true' : undefined}
                aria-describedby={dobFuture ? 'demographics-dob-error' : age !== null ? 'demographics-dob-help' : undefined}
                onChange={(e) => {
                  setDobFuture(false);
                  setDob(e.target.value);
                }}
                onBlur={() => setDobFuture(dobIsFuture)}
              />
            </Field>
          )}
          {office && (
            <Field label="Gender" htmlFor="demographics-gender">
              <SelectField
                size="field"
                id="demographics-gender"
                data-testid="demographics-gender-select"
                value={gender}
                onChange={(e) => setGender(e.target.value)}
              >
                <option value="">Not recorded</option>
                {CLIENT_GENDER_VALUES.map((v) => (
                  <option key={v} value={v}>
                    {humanizeToken(v)}
                  </option>
                ))}
              </SelectField>
            </Field>
          )}
          {office && (
            <Field label="Primary language" htmlFor={languageId}>
              <SelectField
                size="field"
                id={languageId}
                data-testid="about-edit-language"
                value={languageShown}
                onChange={(e) => setLanguagePick(e.target.value)}
              >
                <option value="">Not recorded</option>
                {languages.map((l) => (
                  <option key={l} value={l}>
                    {l}
                  </option>
                ))}
                <option value={LANGUAGE_OTHER}>Other</option>
              </SelectField>
              {languageShown === LANGUAGE_OTHER && (
                <TextInput
                  id={languageOtherId}
                  data-testid="about-edit-language-other"
                  type="text"
                  className={`${FLD} mt-2`}
                  aria-label="Other language"
                  value={languageOther}
                  maxLength={200}
                  onChange={(e) => setLanguageOther(e.target.value)}
                />
              )}
            </Field>
          )}
          <Field label="Relationship status" htmlFor="demographics-relationship">
            <SelectField
              size="field"
              id="demographics-relationship"
              data-testid="about-edit-relationship-status"
              value={relationship}
              onChange={(e) => setRelationship(e.target.value)}
            >
              <option value="">Not recorded</option>
              {RELATIONSHIP_STATUS_VALUES.map((v) => (
                <option key={v} value={v}>
                  {humanizeToken(v)}
                </option>
              ))}
            </SelectField>
          </Field>
        </AboutGroup>

        {office && (
          <AboutGroup title="Their care">
            <Field label="Service start" htmlFor="demographics-service-start" error={startProblem ?? undefined}>
              <DateInput
                ref={startRef}
                id="demographics-service-start"
                data-testid="about-edit-service-start"
                size="field" className="w-full max-w-[11rem]"
                value={serviceStart}
                aria-invalid={startProblem ? 'true' : undefined}
                aria-describedby={startProblem ? 'demographics-service-start-error' : undefined}
                onChange={(e) => {
                  setStartProblem(null);
                  setServiceStart(e.target.value);
                }}
              />
            </Field>
            <Field label="Preferred hospital" htmlFor={hospitalId} help={PLACE_HELP}>
              <DirectoryNameBox
                id={hospitalId}
                testId="about-edit-preferred-hospital"
                value={hospital.text}
                onChange={(text) => setHospital({ text, link: null })}
                onPick={(entry) => setHospital({ text: entry.name, link: entry.id })}
                only="organization"
                listLabel="Directory hospitals"
                linked={hospital.link !== null}
                boxAria={fieldBoxAria(hospitalId, { help: true })}
              />
            </Field>
            <Field label="Pharmacy" htmlFor={pharmacyId} help={PLACE_HELP}>
              <DirectoryNameBox
                id={pharmacyId}
                testId="about-edit-pharmacy"
                value={pharmacy.text}
                onChange={(text) => setPharmacy({ text, link: null })}
                onPick={(entry) => setPharmacy({ text: entry.name, link: entry.id })}
                only="organization"
                listLabel="Directory pharmacies"
                linked={pharmacy.link !== null}
                boxAria={fieldBoxAria(pharmacyId, { help: true })}
              />
            </Field>
          </AboutGroup>
        )}

        {clinical && (
          <AboutGroup title="Consent">
            <div className="sm:col-span-2">
              <Field label="Transportation consent" group htmlFor={ABOUT_CONSENT_ID} help={consentIntro(vitals.pronouns)}>
                <SegmentedToggle
                  options={ABOUT_CONSENT_OPTIONS}
                  value={consent}
                  onChange={(next) => {
                    setConsentError(null);
                    setConsent(next);
                  }}
                  name="Transportation consent"
                  labelledBy={`${ABOUT_CONSENT_ID}-label`}
                  describedBy={consentAria['aria-describedby']}
                  invalid={false}
                  testIdPrefix={ABOUT_CONSENT_ID}
                  id={ABOUT_CONSENT_ID}
                />
              </Field>
              {consentError !== null && (
                <p role="alert" data-testid="about-edit-consent-error" className="mt-2 text-xs text-pepper">
                  {consentError}
                </p>
              )}
            </div>
          </AboutGroup>
        )}

        {saveError !== null && (
          <p role="alert" data-testid="about-edit-error" className="text-xs text-pepper">
            {saveError}
          </p>
        )}
      </div>
    </Dialog>
  );
}

/** Trimmed, with empty as null: the same normalisation the server
 *  applies, so a trailing-space edit or a missing field never reads as
 *  changed. */
function normAddressValue(v: string): string | null {
  const t = v.trim();
  return t === '' ? null : t;
}

const EMPTY_ADDRESS_VALUES: AddressFieldsValues = { line1: '', line2: '', city: '', state: '', zip: '' };

/** A stored row's five fields as the form holds them. */
function addressValuesOf(row: AddressRow | null): AddressFieldsValues {
  return {
    line1: row?.line1 ?? '',
    line2: row?.line2 ?? '',
    city: row?.city ?? '',
    state: row?.state ?? '',
    zip: row?.zip ?? '',
  };
}

/** The line under Street when every field of the home is cleared: the
 *  home comes off through Archive home address, never by emptying its
 *  boxes. LP-40: the word is Archive; nothing on the record is deleted. */
const REMOVE_INSTEAD_TEXT = 'To take this address off, use Archive home address.';

/** True when a stored row lacks any part a home needs. */
function rowHasHomeGaps(row: AddressRow): boolean {
  return missingHomeFields({ line1: row.line1, city: row.city, state: row.state, zip: row.zip }).length > 0;
}

/**
 * ReachEditDialog: the Contact band's ONE "Edit", covering phone, email
 * and the home address together, in that order. The address group shares
 * AddressFields with the Addresses card's own dialog, so the five inputs
 * are defined once. Phone and email save through
 * useSetClientDemographics; the address saves through
 * useSaveClientAddress, the same route the Addresses card uses.
 *
 * The Home address group has three states, by the client's active rows:
 *   - a primary exists: the fields edit that row, and Remove archives it
 *     (one archive POST on Save, no PUT and no check; Undo brings the
 *     fields back as they were). The removed address stays on the
 *     Addresses card under Archived;
 *   - no active row: empty fields whose save adds the home address;
 *   - active rows but no primary: a chooser, set when the dialog opens.
 *     Choosing a complete row makes it the primary with one PUT of that
 *     row exactly as stored (so nothing is re-geocoded or checked);
 *     choosing a row with gaps opens its fields with the gaps flagged,
 *     and the completed row is saved as the home. "Add a new address"
 *     opens the fields, and a new address with the same text as one of
 *     the client's (addressDuplicateKey) cannot be saved: the line under
 *     the fields sends the person back to the chooser. Nothing is chosen
 *     on open, and a save with nothing chosen sends no address.
 *
 * A new address's State starts on the tenant's first working state. In
 * the two cases that start empty, an untouched State equal to that start
 * counts as blank, so on open nothing is flagged, an unchanged Save
 * closes with nothing sent, and a phone-only change sends no address and
 * no check. The home needs a street, city, state and ZIP once any address
 * field holds a value.
 *
 * "Save contact details" is pressable at rest and disabled only while a
 * write or the check runs. Pressed with nothing to send it closes.
 * Pressed with a gap (a short phone, a street missing beside other
 * fields, an address cleared instead of removed, a duplicate of one of
 * the client's addresses, a ZIP that is not one, a home missing a part)
 * it sends nothing, shows each gap's sentence under its box and moves
 * focus to the first gap in the dialog's order. The email is lower-cased
 * when the person leaves a box they typed in; a shape problem shows its
 * one sentence and never blocks the save.
 *
 * An address whose text changed is checked before anything is written,
 * the phone included (useAddressCheck): a match writes; a different match
 * or no match asks inside the Home address group and writes nothing until
 * a choice; a check that cannot run writes and says so. A PO Box line 1,
 * or a seat the check refuses (canCheckAddress), writes with no check.
 * Every input is locked while the check runs, and Cancel writes nothing,
 * before or after it.
 *
 * Whichever of the two writes changed run together with
 * Promise.allSettled, under one local saving state that disables the
 * primary until both settle. The dialog closes only when every write sent
 * succeeded and the address, if sent, needs no word about the map; an
 * approximate, not-found or unchecked address, or a saved-as-typed one
 * the save placed, keeps the dialog open on its line with everything
 * locked and only Done. A failed part's line stays in the one alert area
 * (with the address's line when the address saved), and the other part,
 * having already saved, drops out of the diff on retry, since change
 * detection reads the LIVE `vitals` / `homeAddress` props at save time
 * rather than a value captured on open. Order is not pinned, since the
 * two writes touch different tables; cancelling mid-save may unmount the
 * dialog while one write finishes, which is acceptable.
 */
function ReachEditDialog({
  vitals,
  homeAddress,
  activeAddresses,
  onClose,
}: {
  vitals: VitalsBandData;
  /** The client's home address: the active primary, or null. */
  homeAddress: AddressRow | null;
  /** The client's active addresses, in the record's order: the chooser's
   *  rows, and what a new address is checked against. */
  activeAddresses: readonly AddressRow[];
  onClose: () => void;
}): ReactNode {
  const { tenantSlug = '', clientId = '' } = useParams<{ tenantSlug: string; clientId: string }>();
  const setDemographics = useSetClientDemographics(tenantSlug);
  const saveAddress = useSaveClientAddress(tenantSlug, clientId);
  const archiveAddress = useSetClientAddressArchived(tenantSlug, clientId);
  const contact = useSupportContact();
  const workingStates = readWorkingStates(useTenantConfig(tenantSlug).data);
  const check = useAddressCheck(tenantSlug);
  const checkAdmits = canCheckAddress(useTenant().whoami);
  // The home address is the office's (manage_client_identity), held by a
  // seat that keeps the record (canWriteClinicalRecord, the address
  // routes' server guard, LP-48): every other seat that opens this dialog
  // edits phone, email and preferred name, and no address write is ever
  // built for it.
  const reachWhoami = useTenant().whoami;
  const office = canManageClientIdentity(reachWhoami) && canWriteClinicalRecord(reachWhoami);
  // Phone shows formatted, stores normalized (Design Pass 3, 1b).
  const [phone, setPhone] = useState(formatPhoneDisplay(vitals.phone ?? ''));
  const [phoneMessage, setPhoneMessage] = useState<string | null>(null);
  const [email, setEmail] = useState(vitals.email ?? '');
  const [preferredName, setPreferredName] = useState(vitals.preferredName ?? '');
  // The email's shape sentence, shown after leaving a box the person
  // typed in; the next keystroke clears it. Advisory: it never blocks.
  const [emailProblem, setEmailProblem] = useState<string | null>(null);
  const emailTyped = useRef(false);
  // A press that found the home cleared instead of removed: the Remove
  // line shows as the Street error until the fields change.
  const [pressedClearing, setPressedClearing] = useState(false);
  const phoneRef = useRef<HTMLInputElement | null>(null);
  const [addressValues, setAddressValues] = useState<AddressFieldsValues>(() => addressValuesOf(homeAddress));
  const [stateTouched, setStateTouched] = useState(false);
  const [flagged, setFlagged] = useState<FlaggedFields>(() => flaggedOnOpen(homeAddress));
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  // The address's saved line, when it is worth one; `done` locks the
  // dialog on it once every write sent has succeeded.
  const [saved, setSaved] = useState<SavedLine | null>(null);
  const [done, setDone] = useState(false);
  // Active rows but no primary: the chooser, decided once on open so the
  // group does not change shape under the person when the record re-reads.
  const [chooser] = useState(() => office && homeAddress === null && activeAddresses.length > 0);
  const [choice, setChoice] = useState<string | null>(null);
  const [removing, setRemoving] = useState(false);
  const [pending, setPending] = useState<ClientAddressInput | null>(null);

  const emailToSend = email.trim().toLowerCase();
  const emailChanged = email !== (vitals.email ?? '') && emailToSend !== (vitals.email ?? '');
  const preferredNameChanged = normAddressValue(preferredName) !== normAddressValue(vitals.preferredName ?? '');
  const demographicsChanged =
    normalizePhone(phone) !== normalizePhone(vitals.phone ?? '') || emailChanged || preferredNameChanged;

  // The chooser only matters while the client still has no home; once one
  // is saved (and the record re-read), the address drops out of the diff.
  const choosing = chooser && homeAddress === null;
  const chosenRow =
    choosing && choice !== null && choice !== 'new' ? (activeAddresses.find((a) => a.id === choice) ?? null) : null;
  const chosenRowHasGaps = chosenRow !== null && rowHasHomeGaps(chosenRow);
  const fieldsInPlay = office && !removing && (!chooser || choice === 'new' || chosenRowHasGaps);
  const canRemove = office && homeAddress !== null && !chooser;

  // A new address (no active row, or the chooser's "Add a new address")
  // starts its State on the first working state; untouched, that start is
  // blank to every "did the person type an address" test below.
  const editingNew = homeAddress === null && (!chooser || choice === 'new');
  const untouchedStart = editingNew && !stateTouched;
  const shownValues: AddressFieldsValues = untouchedStart
    ? { ...addressValues, state: newAddressStartState(workingStates) }
    : addressValues;
  const typedValues: AddressFieldsValues = untouchedStart ? { ...addressValues, state: '' } : addressValues;

  const anyAddressFieldFilled = Object.values(typedValues).some((v) => v.trim() !== '');
  const streetEmpty = typedValues.line1.trim() === '';
  const otherAddressFieldsFilled = (['line2', 'city', 'state', 'zip'] as const).some(
    (k) => typedValues[k].trim() !== '',
  );
  const streetEmptyWithOther = fieldsInPlay && streetEmpty && otherAddressFieldsFilled;
  const clearingExistingAddress = office && !removing && !chooser && homeAddress !== null && !anyAddressFieldFilled;
  const storedZip = chooser ? (chosenRow?.zip ?? null) : (homeAddress?.zip ?? null);
  const newAddressIsDuplicate =
    choosing &&
    choice === 'new' &&
    activeAddresses.some((a) => addressDuplicateKey(a) === addressDuplicateKey(shownValues));
  const addressChanged = chooser
    ? chosenRow !== null || (choosing && choice === 'new' && !streetEmpty && !newAddressIsDuplicate)
    : normAddressValue(typedValues.line1) !== normAddressValue(homeAddress?.line1 ?? '') ||
      normAddressValue(typedValues.line2) !== normAddressValue(homeAddress?.line2 ?? '') ||
      normAddressValue(typedValues.city) !== normAddressValue(homeAddress?.city ?? '') ||
      normAddressValue(typedValues.state) !== normAddressValue(homeAddress?.state ?? '') ||
      normAddressValue(normalizeZip(typedValues.zip)) !== normAddressValue(normalizeZip(homeAddress?.zip ?? ''));
  const addressWillSend = office && !removing && addressChanged;
  // What the address write would carry: a complete chosen row as stored,
  // otherwise the fields as shown.
  const sendValues =
    chosenRow !== null && !chosenRowHasGaps ? addressValuesOf(chosenRow) : shownValues;

  const homeRequired = fieldsInPlay && anyAddressFieldFilled;
  const messages = clearingExistingAddress
    ? {}
    : homeFieldMessages({ values: shownValues, flagged, homeRequired, stateRequired: false, storedZip });

  const checking = check.phase.kind === 'checking';
  const prompting = check.phase.kind === 'differs' || check.phase.kind === 'not_found';
  const writing = setDemographics.isPending || saveAddress.isPending || archiveAddress.isPending;
  const busy = writing || checking;
  const locked = done || checking;
  // A new address that duplicates one of the client's is never sent, but
  // it is something the person typed: the press says so rather than close.
  const changed = demographicsChanged || addressWillSend || removing || newAddressIsDuplicate;

  // Only changed fields travel ('' clears to null, not recorded). The
  // email travels trimmed and lower-cased, whether or not the box was left.
  function buildDemographicsFields(): Record<string, string | null> {
    const out: Record<string, string | null> = {};
    if (normalizePhone(phone) !== normalizePhone(vitals.phone ?? '')) {
      out.phone = phone.trim() === '' ? null : normalizePhone(phone);
    }
    if (emailChanged) out.email = emailToSend === '' ? null : emailToSend;
    if (preferredNameChanged) out.preferred_name = normAddressValue(preferredName);
    return out;
  }

  /** The gaps a press would find, each with the box to focus, in the
   *  dialog's order: phone, street, city, state, ZIP. */
  function gaps(): { phone: string | null; street: boolean; clearing: boolean; duplicate: boolean; home: boolean } {
    const phoneGap = phoneProblem(phone);
    if (removing) return { phone: phoneGap, street: false, clearing: false, duplicate: false, home: false };
    const home =
      (fieldsInPlay && zipProblem(shownValues.zip, storedZip) !== null) ||
      (addressWillSend && missingHomeFields(sendValues).length > 0);
    return {
      phone: phoneGap,
      street: streetEmptyWithOther,
      clearing: clearingExistingAddress,
      duplicate: newAddressIsDuplicate,
      home,
    };
  }

  /** "Save contact details": nothing to send closes; a gap says so under
   *  its box and moves focus to the first; otherwise it saves. */
  function pressSave(): void {
    if (busy || done) return;
    if (!changed) {
      check.cancel();
      onClose();
      return;
    }
    const g = gaps();
    setPhoneMessage(g.phone);
    setPressedClearing(g.clearing);
    if (g.home) setFlagged({ city: true, state: true, zip: true });
    let first: HTMLElement | null = null;
    if (g.phone !== null) first = phoneRef.current;
    else if (g.street || g.clearing || g.duplicate) first = document.getElementById('reach-address-address-line1');
    else if (g.home) {
      const missing = new Set(missingHomeFields(sendValues));
      const zipBad = zipProblem(shownValues.zip, storedZip) !== null;
      const part = (['city', 'state', 'zip'] as const).find((f) => missing.has(f) || (f === 'zip' && zipBad));
      first = part === undefined ? null : document.getElementById(`reach-address-address-${part}`);
    }
    if (g.phone !== null || g.street || g.clearing || g.duplicate || g.home) {
      first?.focus();
      return;
    }
    void onSave();
  }

  // An edit is a full replace: the row's own label and both flags travel
  // unchanged alongside the edited fields. An add is always the home
  // address: label 'home', primary, not the current location. A complete
  // row picked in the chooser travels exactly as stored, only made
  // primary, so its text (and its point on the map) is untouched; a row
  // with gaps travels with its completed fields.
  function buildAddressInput(): ClientAddressInput {
    if (chosenRow !== null && !chosenRowHasGaps) {
      return {
        id: chosenRow.id,
        label: chosenRow.label,
        line1: chosenRow.line1,
        line2: chosenRow.line2 ?? null,
        city: chosenRow.city ?? null,
        state: chosenRow.state ?? null,
        zip: chosenRow.zip ?? null,
        is_current_location: chosenRow.isCurrentLocation,
        is_primary: true,
      };
    }
    const fields = {
      line1: shownValues.line1.trim(),
      line2: normAddressValue(shownValues.line2),
      city: normAddressValue(shownValues.city),
      state: normAddressValue(shownValues.state),
      zip: normAddressValue(shownValues.zip) === null ? null : normalizeZip(shownValues.zip),
    };
    if (chosenRow !== null) {
      return {
        id: chosenRow.id,
        label: chosenRow.label,
        is_primary: true,
        is_current_location: chosenRow.isCurrentLocation,
        ...fields,
      };
    }
    if (homeAddress !== null) {
      return {
        id: homeAddress.id,
        label: homeAddress.label,
        is_primary: homeAddress.isPrimary,
        is_current_location: homeAddress.isCurrentLocation,
        ...fields,
      };
    }
    return { label: 'home', is_primary: true, is_current_location: false, ...fields };
  }

  /** Every write this save sends, together: the demographics when they
   *  changed, and the address (a PUT) or its removal (an archive POST). */
  async function writeAll(address: ClientAddressInput | null, how: AddressSaveHow): Promise<void> {
    const removeId = removing ? (homeAddress?.id ?? null) : null;
    const results = await Promise.allSettled([
      demographicsChanged
        ? setDemographics.mutateAsync({ id: clientId, fields: buildDemographicsFields() })
        : Promise.resolve(),
      removeId !== null
        ? archiveAddress.mutateAsync({ addressId: removeId, archived: true }).then(() => undefined)
        : address !== null
          ? saveAddress.mutateAsync(address)
          : Promise.resolve(undefined),
    ]);
    const errors: string[] = [];
    if (results[0]!.status === 'rejected') {
      errors.push(`Phone and email: ${pageWriteText(results[0]!.reason, contact)}`);
    }
    const addressResult = results[1]!;
    if (addressResult.status === 'rejected') {
      errors.push(`Home address: ${saveErrorMessage(addressResult.reason, contact)}`);
    }
    const line =
      addressResult.status === 'fulfilled' && addressResult.value !== undefined
        ? savedLineFor(addressResult.value.geocode_outcome, how, address?.state ?? null)
        : null;
    setSaved(line);
    if (errors.length > 0) {
      setSaveErrors(errors);
      return;
    }
    if (line !== null) {
      setDone(true);
      return;
    }
    onClose();
  }

  async function onSave(): Promise<void> {
    setSaveErrors([]);
    setSaved(null);
    if (removing) {
      await writeAll(null, 'checked');
      return;
    }
    const address = addressWillSend ? buildAddressInput() : null;
    let how: AddressSaveHow = 'checked';
    if (address !== null) {
      // A new address, or one whose text changed from the row it edits, is
      // checked before anything is written; a complete chosen row sent as
      // stored is not.
      const before = chosenRow ?? homeAddress;
      const fields = postalFieldsOf(address);
      const textChanged = before === null || addressTextChanged(postalFieldsOfRow(before), fields);
      // A PO Box is placed at town level, approximate by design, and a
      // seat the check refuses gets no check: both write straight away.
      if (textChanged && !isPoBoxLine(address.line1) && checkAdmits) {
        setPending(address);
        const next = await check.run(fields);
        if (next.kind === 'wait') return;
        how = next.how;
      }
    }
    await writeAll(address, how);
  }

  function chooseFromPrompt(how: AddressSaveHow, useSuggestion: boolean): void {
    const phase = check.phase;
    if (pending === null) return;
    const address = useSuggestion && phase.kind === 'differs' ? { ...pending, ...phase.suggestion } : pending;
    check.clear();
    void writeAll(address, how);
  }

  function cancel(): void {
    check.cancel();
    onClose();
  }

  function pickChoice(next: string): void {
    check.clear();
    setChoice(next);
    if (next === 'new') {
      setAddressValues(EMPTY_ADDRESS_VALUES);
      setStateTouched(false);
      setFlagged(flaggedOnOpen(null));
      return;
    }
    const row = activeAddresses.find((a) => a.id === next) ?? null;
    if (row !== null && rowHasHomeGaps(row)) {
      // A row with gaps opens its fields with the gaps flagged at once.
      setAddressValues(addressValuesOf(row));
      setFlagged(flaggedOnOpen(row));
    }
  }

  function setField(field: keyof AddressFieldsValues): (v: string) => void {
    return (v: string) => {
      check.clear();
      setPressedClearing(false);
      if (field === 'state') setStateTouched(true);
      if (field === 'city' || field === 'state' || field === 'zip') setFlagged((f) => ({ ...f, [field]: false }));
      setAddressValues((prev) => ({ ...prev, [field]: v }));
    };
  }

  return (
    <Dialog
      title="Edit contact"
      size="xl"
      testId="reach-edit-dialog"
      onClose={cancel}
      footer={
        done ? (
          <Button variant="primary" size="sm" data-testid="address-geocode-done" onClick={onClose}>
            Done
          </Button>
        ) : (
          <>
            <Button variant="ghost" size="sm" onClick={cancel}>
              Cancel
            </Button>
            {prompting ? (
              <AddressCheckChoices
                phase={check.phase}
                busy={writing}
                onKeep={() => chooseFromPrompt('checked', false)}
                onUse={() => chooseFromPrompt('checked', true)}
                onSaveAsTyped={() => chooseFromPrompt('saved_as_typed', false)}
              />
            ) : (
              <Button
                variant="primary"
                size="sm"
                disabled={busy || done}
                loading={busy}
                data-testid="reach-edit-save"
                onClick={pressSave}
              >
                Save contact details
              </Button>
            )}
          </>
        )
      }
    >
      <div className="grid grid-cols-1 gap-x-4 gap-y-3 py-2 sm:grid-cols-2">
        <Field label="Phone" htmlFor="reach-phone">
          <input
            ref={phoneRef}
            id="reach-phone"
            data-testid="demographics-phone-input"
            type="tel"
            className={FLD}
            value={phone}
            disabled={locked}
            onChange={(e) => {
              check.clear();
              setPhoneMessage(null);
              setPhone(formatPhoneInput(e.target.value));
            }}
            onBlur={() => setPhoneMessage(phoneProblem(phone))}
          />
          {phoneMessage !== null && (
            <p data-testid="demographics-phone-problem" className="mt-1.5 text-title-sm text-pepper">
              {phoneMessage}
            </p>
          )}
        </Field>
        <Field label="Email" htmlFor="reach-email">
          <input
            id="reach-email"
            data-testid="demographics-email-input"
            type="email"
            spellCheck={false}
            autoCapitalize="none"
            autoComplete="off"
            className={FLD}
            value={email}
            disabled={locked}
            onFocus={() => {
              emailTyped.current = false;
            }}
            onChange={(e) => {
              check.clear();
              emailTyped.current = true;
              setEmailProblem(null);
              setEmail(e.target.value);
            }}
            onBlur={() => {
              // An untouched box keeps the stored value, so tabbing
              // through never makes an edit.
              if (!emailTyped.current) return;
              const lowered = email.trim().toLowerCase();
              setEmail(lowered);
              setEmailProblem(lowered === (vitals.email ?? '') ? null : contactEmailProblem(lowered));
            }}
          />
          {emailProblem !== null && (
            <p data-testid="demographics-email-problem" className="mt-1.5 text-title-sm text-pepper">
              {emailProblem}
            </p>
          )}
        </Field>
        <Field label="Preferred name" htmlFor="reach-preferred-name">
          <input
            id="reach-preferred-name"
            data-testid="demographics-preferred-name-input"
            type="text"
            autoComplete="off"
            className={FLD}
            value={preferredName}
            disabled={locked}
            onChange={(e) => {
              check.clear();
              setPreferredName(e.target.value);
            }}
          />
        </Field>

        {office && (
          <fieldset className="col-span-full m-0 flex flex-col gap-2 border-0 p-0">
            <legend className="mb-1 flex w-full flex-wrap items-center justify-between gap-2 text-title-sm font-semibold text-sage-800">
              <span>Home address</span>
              {canRemove && !removing && (
                <Button
                  variant="ghost"
                  size="sm"
                  data-testid="reach-home-remove"
                  disabled={busy || done}
                  onClick={() => {
                    check.clear();
                    setRemoving(true);
                  }}
                >
                  Archive home address
                </Button>
              )}
            </legend>
            {chooser && (
              <div data-testid="reach-home-chooser" className="flex flex-col gap-1.5">
                <p data-testid="reach-home-chooser-intro" className="text-xs text-ink-soft">
                  No primary address. Choose which of this client's addresses is their home, or add a new one.
                </p>
                {activeAddresses.map((a) => (
                  <label key={a.id} className="flex cursor-pointer items-start gap-2.5 text-sm text-sage-800">
                    <input
                      type="radio"
                      name="reach-home-choice"
                      data-testid={`reach-home-choice-${a.id}`}
                      className="mt-0.5 h-5 w-5 shrink-0 accent-sage-700"
                      checked={choice === a.id}
                      disabled={locked}
                      onChange={() => pickChoice(a.id)}
                    />
                    <span>
                      {addressLine(a)}
                      {a.isCurrentLocation && (
                        <span data-testid={`reach-home-choice-note-${a.id}`} className="block text-xs text-ink-soft">
                          Currently at. Making this the primary address makes it the client's home, so their county
                          moves with it.
                        </span>
                      )}
                    </span>
                  </label>
                ))}
                <label className="flex cursor-pointer items-center gap-2.5 text-sm text-sage-800">
                  <input
                    type="radio"
                    name="reach-home-choice"
                    data-testid="reach-home-choice-new"
                    className="h-5 w-5 shrink-0 accent-sage-700"
                    checked={choice === 'new'}
                    disabled={locked}
                    onChange={() => pickChoice('new')}
                  />
                  Add a new address
                </label>
              </div>
            )}
            {fieldsInPlay && (
              <AddressFields
                idPrefix="reach-address"
                values={shownValues}
                onLine1Change={setField('line1')}
                onLine2Change={setField('line2')}
                onCityChange={setField('city')}
                onStateChange={setField('state')}
                onZipChange={setField('zip')}
                onFieldBlur={(field) => setFlagged((f) => ({ ...f, [field]: true }))}
                line1Required={anyAddressFieldFilled}
                homeRequired={homeRequired}
                line1Error={
                  streetEmptyWithOther
                    ? 'Add the street, or clear the address'
                    : pressedClearing && clearingExistingAddress
                      ? REMOVE_INSTEAD_TEXT
                      : undefined
                }
                line1Help={clearingExistingAddress && !pressedClearing ? REMOVE_INSTEAD_TEXT : undefined}
                cityError={messages.cityError}
                stateError={messages.stateError}
                zipError={messages.zipError}
                disabled={locked}
              />
            )}
            {removing && (
              <div className="flex flex-col items-start gap-1.5">
                <p data-testid="reach-home-remove-line" className="text-xs text-sage-800">
                  This address will be archived when you save. It stays on the Addresses card in Life &amp;
                  logistics, where you can show it and restore it.
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  data-testid="reach-home-remove-undo"
                  disabled={busy || done}
                  onClick={() => setRemoving(false)}
                >
                  Undo
                </Button>
              </div>
            )}
            {newAddressIsDuplicate && (
              <p data-testid="reach-home-duplicate" className="text-xs text-pepper">
                This client already has this address. Choose it above.
              </p>
            )}
            <AddressCheckPrompt phase={check.phase} />
          </fieldset>
        )}

        {saved !== null && (
          <div className="sm:col-span-2">
            <SavedLineView line={saved} />
          </div>
        )}

        {saveErrors.length > 0 && (
          <div role="alert" data-testid="reach-edit-error" className="text-xs text-pepper sm:col-span-2">
            {saveErrors.map((msg) => (
              <p key={msg} className="m-0">
                {msg}
              </p>
            ))}
          </div>
        )}
      </div>
    </Dialog>
  );
}

/** Whole years between a dob and the viewing day (never negative). */
export function ageOn(dobIso: string, todayIso: string): number {
  const [dy, dm, dd] = dobIso.split('-').map(Number);
  const [ty, tm, td] = todayIso.split('-').map(Number);
  let age = (ty ?? 0) - (dy ?? 0);
  if ((tm ?? 0) < (dm ?? 0) || ((tm ?? 0) === (dm ?? 0) && (td ?? 0) < (dd ?? 0))) age -= 1;
  return Math.max(0, age);
}

/** A quiet "Not recorded" value (the empty state lives INSIDE the band —
 *  DP4 §C.4 parity: the layout looks intentional empty and full). `text`
 *  names a more exact absence in the same quiet style. */
export function NotRecorded({ text = 'Not recorded' }: { text?: string }): ReactNode {
  return <span className="italic text-ink-soft">{text}</span>;
}

/** A place fact (LP-33): the name, its phone formatted beside it when the
 *  Directory knows one; Not recorded when there is none. */
function placeFact(place: ClientPlace | null): ReactNode {
  if (place === null) return <NotRecorded />;
  return place.phone !== null ? `${place.name} · ${formatPhoneDisplay(place.phone)}` : place.name;
}

export function VitalsBand({
  vitals,
  addresses = [],
}: {
  vitals: VitalsBandData;
  /** The record's addresses — the Reach band's home / currently-at facts
   *  (DP4 §C.1: contact facts separate from identity facts). */
  addresses?: readonly AddressRow[];
}): ReactNode {
  const { tenantSlug = '', clientId = '' } = useParams<{ tenantSlug: string; clientId: string }>();
  // Board-observer split (0152): the About/Contact "Edit" affordances are
  // client-identity writes — only manage_client_identity holders see them.
  // The read-only board_observer opens the record but edits nothing.
  const { whoami } = useTenant();
  const canEditDetails = canEditClientDetails(whoami);
  // Change client status is the office's (manage_client_identity), as the header chip's door is.
  const canChangeStatus = canManageClientIdentity(whoami);
  // Page-injected local day (TodayContext) — "Last touched today" beats a
  // date the reader has to do arithmetic on; absent provider = absolute.
  const today = useToday();
  // One dialog per band, not one per fact: About's Edit opens
  // AboutEditDialog; Contact's Edit opens ReachEditDialog, which covers
  // phone, email and the home address together.
  const [aboutEditOpen, setAboutEditOpen] = useState(false);
  const [reachEditOpen, setReachEditOpen] = useState(false);
  // G5 (Soak #2): the information overview (the About + Contact facts)
  // collapses/expands, default open, the choice persisted per browser.
  // The action-gating CriticalStrip above never folds — only these
  // ambient identity/contact facts do.
  const [overviewOpen, setOverviewOpen] = useState<boolean>(() => {
    try {
      return window.localStorage.getItem(VITALS_OVERVIEW_KEY) !== 'closed';
    } catch {
      return true;
    }
  });
  const toggleOverview = (): void =>
    setOverviewOpen((v) => {
      const next = !v;
      try {
        window.localStorage.setItem(VITALS_OVERVIEW_KEY, next ? 'open' : 'closed');
      } catch {
        /* localStorage unavailable (private mode) — state stays in memory. */
      }
      return next;
    });

  const activeAddresses = addresses.filter((a) => a.isActive);
  // Home reads homeFact and Currently at the current location when it is
  // another row (src/lib/clientLocation.ts): the Addresses card and the
  // face sheet read the same rules, so all three say the same thing.
  const home = homeFact(addresses);
  const currently = currentlyAt(addresses);

  const rowCls = 'px-4 py-3 flex flex-wrap items-start gap-x-8 gap-y-3';
  // The band header out-ranks the fact labels (Soak #2): darker (sage-800
  // vs the sage-400 fact labels), bolder, a touch larger — so "About" /
  // "Contact" read as section headers, not as another Date-of-birth label.
  const eyebrowCls =
    // Ratified scale: the section name is the working register's smallest
    // TITLE, not a label — it must outrank its fields (Soak #2, preserved).
    'w-16 shrink-0 pt-px text-title-sm font-semibold uppercase tracking-label text-sage-800';
  const editBtn = (testId: string, onClick: () => void, label = 'Edit'): ReactNode => (
    <Button variant="ghost" size="sm" data-testid={testId} onClick={onClick}>
      {label}
    </Button>
  );

  return (
    // DP4 §C.1 (folded into Design Pass 3): the crowded single strip
    // splits into TWO quiet bands — About (identity facts) and Reach
    // (contact facts) — because "demographics contains contact
    // information and those should be separate" (founder). The page owns
    // the card chrome; the rows share one rhythm with the critical strip.
    <div data-testid="vitals-band" className="flex flex-col">
      {/* G5 (Soak #2): the information overview folds. This disclosure
          header is the only new chrome; the action-gating CriticalStrip
          above never collapses — only these ambient facts do. */}
      <div className="flex items-center border-b border-sage-800/[6%] px-3 py-1.5">
        <Button
          variant="ghost"
          size="sm"
          className="!px-1.5 !py-1"
          data-testid="vitals-overview-toggle"
          aria-expanded={overviewOpen}
          // Only reference the overview while it is mounted (Fix 5).
          aria-controls={overviewOpen ? 'vitals-overview' : undefined}
          icon={
            overviewOpen ? (
              <ChevronDown size={15} aria-hidden="true" />
            ) : (
              <ChevronRight size={15} aria-hidden="true" />
            )
          }
          onClick={toggleOverview}
        >
          <span className="text-label tracking-label font-semibold uppercase text-sage-400">
            Information
          </span>
        </Button>
      </div>

      {overviewOpen && (
        <div id="vitals-overview" className="flex flex-col divide-y divide-sage-800/[6%]">
          {/* Contact leads (founder: Contact above About). testid stays
              `vitals-reach` — the row moved, its wiring did not. */}
          <div data-testid="vitals-reach" className={rowCls}>
            <span className={eyebrowCls} aria-hidden="true">
              Contact
            </span>

            <Fact label="Phone">
              <span data-testid="vitals-phone">
                {vitals.phone !== null && vitals.phone !== '' ? (
                  formatPhoneDisplay(vitals.phone)
                ) : (
                  <NotRecorded />
                )}
              </span>
            </Fact>

            <Fact label="Email">
              <span data-testid="vitals-email">
                {vitals.email !== null && vitals.email !== '' ? vitals.email : <NotRecorded />}
              </span>
            </Fact>

            <Fact label="Home address">
              <span data-testid="vitals-home-address">
                {home.kind === 'home' ? (
                  addressLine(home.row)
                ) : home.kind === 'no_primary' ? (
                  <NotRecorded text={NO_PRIMARY_TEXT} />
                ) : (
                  <NotRecorded />
                )}
              </span>
            </Fact>

            {currently !== null && (
              <Fact label="Currently at">
                <span data-testid="vitals-currently-at">{addressLine(currently)}</span>
              </Fact>
            )}

            {/* One quiet Edit for the whole band: phone, email and the
                Home address shown above ride the same dialog. The full
                address list keeps its own card on the Life & logistics
                tab. Board-observer split: identity writes, hidden for
                read-only viewers. */}
            {canEditDetails && (
              <span className="ml-auto self-start flex items-center gap-1">
                {editBtn('reach-edit-action', () => setReachEditOpen(true))}
              </span>
            )}
          </div>

          <div data-testid="vitals-about" className={rowCls}>
            <span className={eyebrowCls} aria-hidden="true">
              About
            </span>

            {/* DOB carries the computed age ("March 14, 1941 · 85") — the
                fact a CM actually wants (DP4 §C.1). */}
            <Fact label="Date of birth">
              <span data-testid="vitals-dob">
                {vitals.dob !== null ? (
                  `${proseDate(vitals.dob)} · ${ageOn(vitals.dob, today ?? practiceTodayIso())}`
                ) : (
                  <NotRecorded />
                )}
              </span>
            </Fact>

            <Fact label="Gender">
              <span data-testid="vitals-gender">
                {vitals.gender !== null ? humanizeToken(vitals.gender) : <NotRecorded />}
              </span>
            </Fact>

            {/* LP-33: the facts run in the edit form's order (About them,
                then Their care): language after gender, the two places
                after the service start. */}
            <Fact label="Primary language">
              <span data-testid="vitals-primary-language">
                {vitals.primaryLanguage !== null ? vitals.primaryLanguage : <NotRecorded />}
              </span>
            </Fact>

            <Fact label="Relationship status">
              <span data-testid="vitals-relationship-status">
                {vitals.relationshipStatus !== null ? humanizeToken(vitals.relationshipStatus) : <NotRecorded />}
              </span>
            </Fact>

            <Fact label="Service start">
              <span data-testid="vitals-service-start">
                {vitals.serviceStartDate !== null ? proseDate(vitals.serviceStartDate) : <NotRecorded />}
              </span>
            </Fact>

            <Fact label="Preferred hospital">
              <span data-testid="vitals-preferred-hospital">{placeFact(vitals.preferredHospital)}</span>
            </Fact>

            <Fact label="Pharmacy">
              <span data-testid="vitals-pharmacy">{placeFact(vitals.pharmacy)}</span>
            </Fact>

            {/* The date of death shows when it is recorded, or when the
                client's status says they have died (then as Not recorded).
                Read-only here (LP-25): it is recorded with the status, so a
                viewer who may change the status is told where. */}
            {(vitals.dateOfDeath !== null || isDeceasedStatus(vitals.lifecycleStatus)) && (
              <Fact label="Date of death">
                <span data-testid="vitals-date-of-death">
                  {vitals.dateOfDeath !== null ? proseDate(vitals.dateOfDeath) : <NotRecorded />}
                </span>
                {/* The header chip opens Change status only on a client on
                    the books, so an archived one gets no pointer. */}
                {canChangeStatus && vitals.isActive && (
                  <span data-testid="vitals-date-of-death-where" className="ml-1.5 text-xs font-normal text-ink-soft">
                    Change it from Change status
                  </span>
                )}
              </Fact>
            )}

            {/* UI polish pass (PART 1.2 / PART 2): the engagement chip
                moved UP onto the forest header band (beside the lifecycle
                status pill, ClientRecord.tsx) and the Care manager fact
                moved UP into CriticalStrip (first position) — both
                retired from this row. See ENGAGEMENT_LABELS (now exported)
                and CriticalStrip.tsx's "strip-care-manager" fact.

                The SPICE therapist name and the schedule summary sit there
                too now (CriticalStrip's first slot, engagement-aware) —
                the schedule's own editor lives on the SPICE tab. Neither
                fact nor its edit affordance renders here any more. */}

            {/* UI polish pass (PART 3): Insurance now sits BEFORE Risk
                flags in the row order (was after) — the founder's ruling. */}
            {vitals.coverageSummary && <Fact label="Insurance">{vitals.coverageSummary}</Fact>}

            {/* D8 (Soak #2): the risk line stays, but QUIET and
                de-emphasized (no bold, smaller) so it is not the loudest
                thing here. Its link now SCROLLS to the risk card on the
                Care record (#risk-flags, the G4 hash-scroll consumer),
                not just switches tabs. pepper-deep TEXT (reserved for
                elevated) still carries the signal; never a red slug. */}
            {vitals.riskPills.length > 0 && (
              <Fact label="Risk flags">
                <Link
                  to={`/t/${tenantSlug}/clients/${clientId}?tab=care#risk-flags`}
                  data-testid="vitals-risk-line"
                  className="text-title-sm text-pepper-deep underline decoration-pepper-deep/25 underline-offset-2 hover:decoration-pepper-deep focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 rounded-tight"
                >
                  {vitals.riskPills.length === 1
                    ? `${humanizeToken(vitals.riskPills[0]!.category)} risk elevated · view`
                    : `${vitals.riskPills.length} elevated risks · view`}
                </Link>
              </Fact>
            )}

            {vitals.referredBy && <Fact label="Referred by">{vitals.referredBy.label}</Fact>}

            {vitals.lastTouched && (
              // Relative for the freshest two days ("Today" / "Yesterday"),
              // absolute prose beyond that — capitalized to sit alongside the
              // other Fact values (a prose date starts with a capital month).
              <Fact label="Last updated">
                {capitalize(proseDayOrDate(practiceDayOfStamp(vitals.lastTouched) ?? vitals.lastTouched, today))}
              </Fact>
            )}

            {/* ONE quiet Edit for the whole band (DP4 §C.1): DOB, gender,
                and engagement + SPICE therapist ride the same dialog —
                the per-fact Change/Assign buttons above retired. Care
                manager's own door is CriticalStrip's Assign/Change now.
                Board-observer split (0152): identity write → hidden for
                read-only viewers. */}
            {canEditDetails && (
              <span className="ml-auto self-start">
                {editBtn('demographics-action', () => setAboutEditOpen(true))}
              </span>
            )}
          </div>
        </div>
      )}

      {aboutEditOpen && (
        <AboutEditDialog vitals={vitals} onClose={() => setAboutEditOpen(false)} />
      )}

      {reachEditOpen && (
        <ReachEditDialog
          vitals={vitals}
          homeAddress={home.kind === 'home' ? home.row : null}
          activeAddresses={activeAddresses}
          onClose={() => setReachEditOpen(false)}
        />
      )}
    </div>
  );
}
